// hunt.js — 사냥 설정 에디터 (HuntForm). 선언형 스키마 기반 폼 + mountSlotEditor + #/slot-settings 페이지.
//   공개 API (shared contract):
//     HuntForm.create(container, {settings, readOnly?, live?: ()=>({map_id,x,y})|null, onChange?(dirtyCount)})
//        -> { collect() -> settings (throws HuntForm.ValidationError{errors:[{path,label,message}]}),
//             isDirty(), dirtyCount(), reset(settings), setLive(pos), destroy() }
//     HuntForm.mountSlotEditor(container, {slotId, live?, onSaved?}) -> { isDirty(), destroy() }
//     HuntForm.ValidationError
//   설계 메모:
//     · 상단 필드(leaf)는 점(.) 경로로 독립 추적한다. collect()는 원본(base)의 깊은 복제에서 "수정된 경로만" 덮어쓴다
//       → 알 수 없는 키/노출하지 않은 키는 그대로 보존된다.
//     · rows/travel/map 는 하나의 필드(객체/배열 전체)로 다루되, 원본에서 복제해 재구성하므로 숨은 하위 키도 보존.
//     · Lua가 읽지 않는 필드(pk_response, movement, shop_role, after_return.restock, after_return.point.map_id,
//       return_scroll 의 teleport_cost/min_saving/walk_radius/merchant_point)와 내부 키(hunt_maps[].origin,
//       npc_travel.npc_point — 등록소 다리가 채움)는 UI를 제공하지 않는다(값은 보존).
'use strict';
window.HuntForm = (function () {
  const App = window.App;
  const esc = App.esc;
  const T = App.t;
  const zh = () => App.lang() === 'zh-CN';
  const clone = (x) => (x == null ? x : JSON.parse(JSON.stringify(x)));
  const isObj = (x) => x && typeof x === 'object' && !Array.isArray(x);

  class ValidationError extends Error {
    constructor(errors) { super('invalid hunt settings'); this.name = 'ValidationError'; this.errors = errors || []; }
  }

  // ── strings ──
  App.i18n.add({
    ko: {
      hunt_title: '사냥 설정', hunt_search_ph: '설정 검색…', hunt_no_results: '검색 결과가 없습니다.',
      hunt_add_map: '사냥 맵 추가', hunt_add_row: '+ 추가', hunt_remove: '삭제',
      hunt_rows_empty: '항목이 없습니다. "+ 추가"로 만들고, 항목을 누르면 편집합니다.', hunt_row_blank: '(빈 항목)', hunt_row_incomplete: '필수 항목을 입력하세요.',
      hunt_points_n: '순회 좌표 {0}개', hunt_here: '현재 위치 추가', hunt_clear_points: '좌표 전체 삭제',
      hunt_canvas_hint: '맵을 클릭하면 좌표 추가 · 드래그로 이동 · 휠로 확대/축소 · 점을 드래그해 옮기고 ×로 삭제',
      hunt_me: '내 위치', hunt_not_on_map: '캐릭터가 이 맵에 없어 현재 위치를 쓸 수 없습니다.',
      hunt_map_pick_first: '먼저 맵을 선택하세요.',
      hunt_travel_unset: '미설정', hunt_travel_summary: '두루마리 {0}',
      hunt_tag_ph: '입력 후 Enter',
      hunt_err_num: '숫자를 입력하세요.', hunt_err_int: '정수를 입력하세요.', hunt_err_min: '{0} 이상이어야 합니다.',
      hunt_err_max: '{0} 이하여야 합니다.', hunt_err_json: 'JSON 형식이 올바르지 않습니다.',
      hunt_err_json_obj: '설정은 객체(JSON)여야 합니다.', hunt_err_required: '필수 항목입니다.',
      hunt_json_apply: '폼에 적용', hunt_json_refresh: '폼 값 불러오기',
      hunt_json_hint: '전문가용. 전체 설정을 직접 편집합니다. “폼에 적용”을 누르면 검증 후 폼에 반영됩니다.',
      hunt_json_applied: 'JSON을 폼에 적용했습니다.', hunt_key: '키',
      // sections
      hunt_sec_ground: '사냥터', hunt_sec_ground_d: '레벨 구간별 사냥 맵과 순회 좌표를 설정합니다.',
      hunt_sec_combat: '전투·타겟', hunt_sec_combat_d: '공격 대상, 우선순위, 카이팅, 화살을 설정합니다.',
      hunt_sec_party: '파티', hunt_sec_party_d: '같은 계정의 캐릭터들끼리 집화·따라가기·줍기 분담. 게임 파티가 아니라 이름 목록으로 동작합니다.',
      hunt_l_party_leader: '리더 캐릭터명', hunt_t_party: '리더가 때리는 몹을 최우선으로 쫓고, 리더 옆(3칸)에서 기다립니다. 화면 밖이면 서버에 위치를 물어 같은 맵의 사냥 구역 안일 때 따라갑니다.',
      hunt_l_party_members: '파티원', hunt_t_party_members: '파티원 이름들(리더 포함). 파티원이 치는 몹은 선점으로 보지 않고, 파티원을 치는 몹을 함께 칩니다. 파티원이 나를 노리는 것(힐)은 PK로 보지 않습니다(실제로 맞으면 PK).',
      hunt_l_party_loot: '줍기 담당', hunt_t_party_loot: '이 캐릭터가 화면에 있으면 다른 파티원은 줍지 않습니다(안 보이면 각자 줍기). 비우면 각자 줍습니다.',
      hunt_l_party_follow: '리더 따라가기', hunt_l_party_focus: '집화(파티원 타겟 우선)',
      hunt_b_stability: '장기 운영', hunt_l_sched_enabled: '가동·휴식 스케줄',
      hunt_l_sched_run: '가동', hunt_l_sched_rest: '휴식', hunt_u_min: '분',
      hunt_t_schedule: '가동 N분 ↔ 휴식 M분을 반복합니다(캐릭터마다 시작 시각을 조금씩 다르게). 휴식 중엔 마을에서 대기합니다.',
      hunt_l_ds_enabled: '연속 사망 보호', hunt_l_ds_count: '연속 사망 기준', hunt_l_ds_rest: '연속 사망 휴식',
      hunt_t_death_streak: '켜면, 1시간 안에 이 횟수만큼 죽을 때 그 시간만큼 마을에서 쉽니다. 접속 끊김 화면은 사망으로 세지 않고, 작업이 재시작되면 기록이 초기화됩니다.',
      hunt_b_reposition: '정체 재배치', hunt_l_rp_income: '수입 없음', hunt_l_rp_position: '제자리',
      hunt_l_rp_nomob: '몹 없음',
      hunt_t_reposition: '사냥 구역 안에서 ① 수입(경험치·전리품·아데나)이 이 시간 동안 없거나 ② 반경 6칸 안을 이 시간 동안 맴돌며 수입도 없거나 ③ 몹이 이 시간 동안 안 보이면, 회귀하기 전에 순회점·처치 장소 중 가장 먼 곳으로 걸어갑니다. 마을 왕복·이동 시간은 세지 않습니다.',
      hunt_sec_survival: '생존', hunt_sec_survival_d: '물약·치유·귀환·회피 등 생존 규칙입니다.',
      hunt_sec_buffs: '버프', hunt_sec_buffs_d: '유지할 버프, 맵별 버프, 이벤트 버프, 헤이스트 상점.',
      hunt_sec_restock: '귀환·보급', hunt_sec_restock_d: '귀환 두루마리, 구매/판매, 귀환 후 행동, 창고, 식사, 마법책.',
      hunt_sec_explore: '탐색·전리품', hunt_sec_explore_d: '맵 탐색과 전리품 수집 규칙.',
      hunt_sec_advanced: '고급', hunt_sec_advanced_d: '타이머, 경로 캡처, 드물게 쓰는 이동·설정. 라벨 옆 회색 글자가 실제 설정 키입니다.',
      hunt_sec_json: 'JSON', hunt_sec_json_d: '전체 설정을 직접 편집(전문가용).',
      // ground
      hunt_l_hunt_maps: '레벨 구간별 사냥 맵', hunt_t_hunt_maps: '캐릭터 레벨이 구간에 들면 그 맵에서 사냥합니다. 좌표를 비우면 도착 지점부터 탐색합니다.',
      hunt_l_patrol_radius: '순회점 도달 반경', hunt_t_patrol_radius: '이 반경 안에 들면 다음 순회점으로 넘어갑니다(0=정확히 도달).',
      hunt_l_lv_min: '레벨 하한', hunt_l_lv_max: '레벨 상한',
      hunt_l_map: '맵', hunt_l_region: '구역', hunt_l_entry: '진입점·회피 타일',
      hunt_l_entry_map: '포털 맵', hunt_l_entry_x: '포털 X', hunt_l_entry_y: '포털 Y',
      hunt_l_push_x: '진입 클릭 X', hunt_l_push_y: '진입 클릭 Y',
      hunt_l_avoid_tiles: '회피 타일', hunt_l_x: 'X', hunt_l_y: 'Y', hunt_l_approach: '접근 경유점',
      // combat / target
      hunt_l_allow_mobs: '공격 허용 몹', hunt_t_allow_mobs: '비우면 모든 몹을 공격합니다. 추가하면 지정한 몹만 공격합니다.',
      hunt_l_ignore_mobs: '무시할 몹 (레벨 구간)', hunt_t_ignore_mobs: '지정한 캐릭터 레벨 구간에서만 무시하고, 이후에는 공격합니다.',
      hunt_l_never_target: '항상 제외할 몹', hunt_t_never_target: '어떤 경우에도 공격하지 않습니다.',
      hunt_l_priority: '몹 우선순위', hunt_t_priority: '가중치가 높을수록 먼저 노립니다(기본 1). 2배 이상이면 현재 대상을 바꿉니다.',
      hunt_l_stick: '한번 공격한 몹 고정', hunt_t_stick: '대상이 죽거나 사라지거나 더 높은 우선순위가 나타날 때까지 그 몹을 계속 공격합니다.',
      hunt_l_attack_others: '다른 유저가 때리는 몹도 공격', hunt_t_attack_others: '끄면(기본) 다른 플레이어·펫·소환수가 노리거나 최근 5초 안에 친 몹은 건너뜁니다. 파티원이 치는 몹은 예외로 함께 칩니다.',
      hunt_l_seek_progress: '추적 진행 판정', hunt_t_seek_progress: '사거리 밖 몹을 쫓다가 이 시간 동안 가까워지지 않으면 그 몹을 20초 동안 건너뜁니다.',
      hunt_b_retaliate: '반격 (무시 몹이 때릴 때)', hunt_t_retaliate: '무시 목록의 몹이 나를 때리면, 무시하지 않고 후순위로 잡습니다(가중치가 시간이 지날수록 올라감).',
      hunt_l_retaliate_mobs: '반격 대상 (무시 몹)', hunt_l_retaliate_base: '반격 시작 가중치', hunt_l_retaliate_step: '가중치 증가량', hunt_l_retaliate_step_sec: '증가 간격',
      hunt_l_attack_in_place: '제자리 공격', hunt_l_attack_hold: '공격 자세 유지', hunt_l_bow_range: '활 사거리',
      hunt_l_kite_en: '카이팅 사용 (활)', hunt_l_kite_skip: '카이팅 제외 (서서 쏨)',
      hunt_l_arrow_priority: '일반 화살 우선순위', hunt_l_undead_arrow: '언데드 화살 우선순위',
      hunt_b_kite_tune: '카이팅 세부 튜닝 (고급)',
      hunt_l_kite_dps: '기본 DPS', hunt_l_kite_ttk: '기본 처치 시간', hunt_l_kite_reserve: '탈출 예비 HP',
      hunt_l_kite_pbudget: '물약 예산', hunt_l_kite_php: '물약당 회복', hunt_l_kite_threat: '위협 반경',
      hunt_l_kite_sbudget: '회복 마법 예산', hunt_l_kite_shp: '마법당 회복',
      hunt_l_kite_gap: '사격 간격', hunt_l_kite_lag: '지연 여유', hunt_l_kite_mingain: '최소 이득',
      hunt_l_kite_cycle: '사격 주기', hunt_l_kite_lock: '사격 잠금', hunt_l_kite_stun: '피격 경직',
      hunt_l_kite_shootmin: '최소 사격 거리', hunt_l_kite_orc: '추격자 보행', hunt_l_kite_walk: '내 보행',
      hunt_l_kite_latency: '네트워크 지연', hunt_l_kite_rmax: '최대 후퇴', hunt_l_kite_rmin: '최소 후퇴',
      hunt_l_kite_race: '경주 탈출 시간', hunt_l_kite_aggr: '공격적',
      hunt_l_kite_levels: '몹별 처치 난이도', hunt_l_kite_aggain: '공격적 가중', hunt_l_kite_open: '개활지 가중',
      hunt_l_kite_los: '시야 가중', hunt_l_kite_leasht: '내 드롭 거리', hunt_l_kite_leashw: '드롭 가중',
      hunt_l_kite_contactd: '접촉 거리', hunt_l_kite_contacth: '접촉 피격 시간', hunt_l_kite_outpace: '추월 이득',
      hunt_l_kite_pincer: '협공 여유', hunt_l_kite_pincere: '협공 탈출', hunt_l_kite_diseng: '접촉 이탈',
      hunt_l_kite_arrival: '도착 마무리', hunt_l_kite_rfinish: '경주 마무리', hunt_l_kite_failretreat: '후퇴 실패 유예',
      hunt_l_kite_corner: '몰리면 맞섬',
      // survival
      hunt_l_heal_hp: '물약 사용 HP', hunt_t_heal_hp: '이 HP% 이하에서 회복약을 씁니다(비우면 물약 회복 안 함).',
      hunt_l_heal_interval: '물약 간격', hunt_l_heal_codes: '회복약 (우선순위 순)',
      hunt_l_heal_spells: '치유 마법', hunt_l_spell: '스펠', hunt_l_hp_pct: 'HP%',
      hunt_l_return: '귀환 조건 (하나라도 충족 시)', hunt_l_cond_type: '조건', hunt_l_item: '아이템',
      hunt_l_qty: '수량', hunt_l_cond_value: '수치',
      hunt_opt_item_below: '아이템 N개 이하', hunt_opt_hp_below: 'HP % 이하', hunt_opt_weight_above: '무게 % 이상',
      hunt_l_reserve_hits: '피격 예비 배수', hunt_t_reserve_hits: '최근 피격 N배 이하의 HP면 귀환(0=사용 안 함).',
      hunt_l_avoid_radius: '위험 몹 회피 반경', hunt_t_avoid_radius: '0~8, 0=끔.',
      hunt_l_avoid_mobs: '회피 대상 몹', hunt_t_avoid_mobs: '이름 일부만 맞아도 위험으로 보고 반경 안에 들면 귀환합니다.',
      hunt_l_return_codes: '귀환 주문서', hunt_t_return_codes: '귀환 두루마리가 없을 때 사용합니다.',
      hunt_b_damage_escape: '피격 탈출', hunt_l_trigger_mobs: '발동 몹', hunt_l_defend_hp: '방어 HP%',
      hunt_l_fallback_travel: '대체 이동', hunt_l_cure_items: '해독 (아이템·스킬)',
      hunt_l_item_codes: '아이템', hunt_l_interval: '간격',
      hunt_t_cure_items: '발동 상태가 걸리면 바로 풉니다: 기본은 가진 아이템 먼저, 없으면 해독 스킬.',
      hunt_l_cure_trigger: '발동 상태(디버프)', hunt_t_cure_trigger: '버프 목록에 뜨는 상태 ID(5319 = 중독)입니다. 거는 버프가 아닙니다.',
      hunt_l_cure_spells: '해독 스킬', hunt_l_cure_prefer: '스킬 먼저(물약 절약)',
      hunt_t_spell_auto: '배우지 않은 스킬은 무시하고, 배웠지만 퀵슬롯에 없으면 F5–F12 빈 칸에 자동 등록합니다.',
      hunt_l_mi_buffs: '유지 확인 버프', hunt_t_mi_buffs: '이 버프가 걸려 있으면 아이템을 쓰지 않습니다(비우면 사용 직후 새로 생긴 버프로 자동 학습).',
      hunt_t_eb_scroll_slot: '비우면 두루마리가 등록된 퀵슬롯을 쓰거나 빈 칸에 자동 등록합니다.',
      // buffs
      hunt_l_maintain_buffs: '유지할 버프', hunt_l_buffs_by_map: '맵별 추가 버프',
      hunt_l_buff_recast: '버프 재시전 간격', hunt_l_maintain_items: '유지 아이템 (버프 물약 등)', hunt_l_mp_below: 'MP% 이하',
      hunt_b_event_buff: '이벤트 버프', hunt_l_eb_enabled: '이벤트 버프 자동 보충', hunt_l_eb_buffids: '버프',
      hunt_l_eb_object: '버프 오브젝트', hunt_l_eb_scroll_code: '두루마리 아이템', hunt_l_eb_scroll_slot: '두루마리 퀵슬롯',
      hunt_l_eb_scroll_title: '두루마리 이름', hunt_l_eb_section: '메뉴 섹션', hunt_l_eb_dest: '목적지',
      hunt_l_eb_town_map: '마을 맵', hunt_l_eb_town_region: '마을 구역', hunt_l_eb_layout: '메뉴 레이아웃',
      hunt_l_eb_wh: '창고 지점', hunt_l_eb_obj_pt: '오브젝트 지점', hunt_l_eb_resume: '복귀 지점',
      hunt_l_eb_resume_map: '복귀 맵', hunt_l_eb_hunt_travel: '사냥터 복귀 이동',
      hunt_b_haste: '헤이스트 상점', hunt_l_haste_en: '사용', hunt_l_haste_names: '장사꾼 이름',
      hunt_l_haste_price: '가격', hunt_l_haste_radius: '찾는 반경', hunt_l_haste_buff: '버프 ID',
      hunt_l_haste_retry: '재시도 간격', hunt_l_haste_full: '충분 기준', hunt_l_haste_timeout: '제한 시간',
      hunt_b_transform: '변신 주문서', hunt_l_tf_en: '사용', hunt_l_tf_items: '변신 주문서', hunt_t_tf_items: '변신 버프가 없을 때 이 주문서를 읽습니다.',
      hunt_l_tf_entry: '변신 목록 항목', hunt_t_tf_entry: '변신 창에서 누를 항목 글자 (예: 동일외형변신(요정))', hunt_l_tf_buffs: '변신 버프 ID', hunt_l_tf_duration: '버프 없을 때 재사용 간격',
      hunt_b_npc_contract: 'NPC 계약·정산', hunt_l_nc_en: '사용', hunt_t_nc_en: 'NPC 이름이 있어야 동작합니다.', hunt_l_nc_npc: '계약 NPC',
      hunt_l_nc_point: 'NPC 옆 지점', hunt_l_nc_scroll: 'NPC 마을 이동', hunt_l_nc_contract: '계약 대화 항목', hunt_l_nc_settle: '정산 대화 항목', hunt_l_nc_renew: '재계약 대화 항목',
      hunt_l_nc_items: '정산 아이템', hunt_l_nc_values: '아이템 가치', hunt_l_nc_settle_value: '정산 시작 가치', hunt_l_nc_kill_names: '계약 몬스터 (이름 일부)',
      hunt_t_nc_kill_names: '이 몬스터를 아이템 없이 여러 번 잡으면 계약이 없는 것으로 보고 다시 계약합니다.', hunt_l_nc_kills: '계약 확인 처치 수',
      hunt_l_hold_town: '마을 대기 (사냥 시작 안 함)', hunt_t_hold_town: '켜면 귀환 후 마을에 머뭅니다. 판매·창고·구매는 계속합니다.',
      hunt_l_world_fps: '월드 표본 수(초당)', hunt_t_world_fps: '웹 지도에 보내는 주변 위치 표본 수 0~30',
      hunt_l_kite_names: '카이팅 대상만 (비우면 전체)', hunt_t_kite_names: '지정하면 이 몬스터만 거리를 유지하며 쏘고 나머지는 서서 쏩니다.',
      hunt_l_loot_kill: '마리당 처치 예상', hunt_t_loot_kill: '내 드롭 보호 시간 안에 전투를 먼저 끝낼지 계산할 때 씁니다.', hunt_l_loot_protect: '내 드롭 보호 시간',
      hunt_l_wh_npcs: '창고지기 이름 (여러 명)', hunt_l_wh_withdraw_menu: '찾기 메뉴 글자', hunt_l_wh_travel: '창고 이동 (두루마리)',
      hunt_l_wh_withdraw_codes: '한 번 찾을 아이템', hunt_l_wh_withdraw_token: '찾기 토큰', hunt_t_wh_withdraw_token: '토큰을 바꾸면 위 아이템을 창고에서 한 번 더 모두 찾습니다.',
      hunt_l_wh_withdraw_names: '찾을 아이템 이름(정확히)', hunt_t_wh_withdraw_names: '창고 목록 글자가 이 이름과 정확히 같은 행만 꺼냅니다(+0 접두·묶음 수는 빼고 비교). 위 아이템이 하나 이상 있어야 찾기가 동작합니다.',
      hunt_l_wh_keep: '가방에 남길 수량', hunt_t_wh_keep: '지정한 아이템들을 합쳐 이 수량만 가방에 남기고, 나머지는 방문할 때마다 무게와 상관없이 맡깁니다.',
      hunt_l_sell_merchant: '판매 상인', hunt_l_trip_min: '원정 최소 수량', hunt_l_sell_point: '상인 옆 지점', hunt_l_sell_travel: '판매 마을 이동',
      hunt_l_haste_hold: '멈춤(디버그)', hunt_l_haste_travel: '이동',
      hunt_l_haste_force: '지금 1회 강제 구매', hunt_t_haste_force: '켜고 저장하면 헤이스트가 걸려 있어도 한 번 교환합니다. 켠 채로 다시 저장할 때마다 또 한 번 실행되니 끝나면 끄세요.',
      // restock
      hunt_b_return_scroll: '귀환 두루마리', hunt_l_return_scroll: '귀환 두루마리',
      hunt_b_restock: '귀환 시 보급', hunt_l_restock_buy: '구매 (목표 수량까지)', hunt_l_restock_sell: '판매',
      hunt_l_target_qty: '목표 수량', hunt_l_minimum_qty: '보충 기준', hunt_l_min_level: '최소 레벨',
      hunt_l_depart_min: '출발 최소 수량', hunt_t_depart_min: '이 수량보다 적으면 사냥을 시작하지 않고 마을에서 보충을 기다리며 웹에 알립니다(예: 화살). 비우면 살 수 있는 만큼 사고 사냥을 이어 갑니다.',
      hunt_l_restock_merchant: '구매 NPC', hunt_l_sell_merchants: '판매 NPC (순서대로)',
      hunt_l_weight_limit: '무게 제한', hunt_t_weight_limit: '이 무게%에서 구매를 멈춥니다.',
      hunt_l_restock_travel: '상점 이동 (필수)', hunt_l_hunt_travel: '사냥터 복귀 이동',
      hunt_badv_buy: '구매 세부', hunt_l_min_adena: '최소 아데나', hunt_l_reserve_adena: '예비 아데나',
      hunt_l_available: '소지 인정 코드', hunt_l_equipped: '장착 인정 코드', hunt_l_purchase_qty: '묶음 구매 수량',
      hunt_l_auto_target: '자동 목표량', hunt_l_max_qty: '최대 수량', hunt_l_learned_spell: '학습 스펠',
      hunt_l_pack_size: '묶음 크기', hunt_l_register_slot: '퀵슬롯 등록', hunt_l_equip: '장착', hunt_l_buy_name: '상점 표기',
      hunt_l_buy_merchant: '구매 NPC', hunt_l_buy_travel: '이동',
      hunt_b_after_return: '귀환 후', hunt_l_town_wait: '마을 대기', hunt_l_resume_hunt: '사냥터 복귀',
      hunt_l_recover_hp: '회복 HP%', hunt_l_ar_map: '안전 맵', hunt_l_ar_region: '안전 구역', hunt_l_ar_point: '귀환 지점 (필수)',
      hunt_b_warehouse: '창고 입고', hunt_l_wh_en: '창고 사용', hunt_l_wh_items: '입고 아이템',
      hunt_l_wh_preserve: '보존 아이템', hunt_l_wh_always: '항상 입고', hunt_l_wh_weight: '시작 무게%',
      hunt_l_wh_target: '목표 무게%', hunt_l_wh_section: '메뉴 섹션', hunt_l_wh_deposit: '입고 메뉴', hunt_l_wh_npc: '창고 NPC',
      hunt_b_food: '자동 식사', hunt_l_food_en: '포만감 관리', hunt_l_food_target: '목표 %', hunt_l_food_min: '시작 %',
      hunt_l_food_interval: '간격', hunt_l_food_codes: '음식 아이템',
      hunt_b_spell_shop: '마법책 상점', hunt_l_ss_buy: '구매', hunt_l_ss_sell: '판매', hunt_l_ss_merchant: '구매 NPC',
      hunt_l_ss_travel: '상점 이동', hunt_l_ss_hunt_travel: '사냥터 복귀 이동',
      hunt_l_learn_books: '마법책 학습', hunt_t_learn_books: '가방에서 책을 읽어 스펠을 배웁니다(레벨당 1회).',
      // explore
      hunt_b_exploration: '탐색', hunt_l_exp_en: '탐색 사용', hunt_l_exp_exclude: '제외 구역',
      hunt_l_exp_margin: '경계 여유', hunt_l_exp_revisit: '재방문 간격', hunt_l_exp_memory: '기억 시간',
      hunt_l_exp_idle: '대기 후 진입', hunt_l_exp_progress: '진행 판정', hunt_l_exp_unreach: '도달 불가 판정',
      hunt_b_loot: '전리품', hunt_l_loot_others: '남의 드롭도 수집', hunt_t_loot_others: '끄면 내 드롭만 줍습니다.', hunt_l_loot_scav: '장비 줍기',
      hunt_l_loot_weight: '무게 제한', hunt_l_loot_own: '내 드롭 우선순위', hunt_l_loot_exclude: '제외 아이템 이름',
      hunt_l_loot_memory: '기억 시간', hunt_l_loot_view: '탐지 거리', hunt_l_loot_return: '복귀 거리',
      hunt_l_loot_near: '즉시 수집 거리', hunt_l_loot_clear: '전투 우선 반경',
      // advanced
      hunt_l_walk_stall: '보행 정체 판정', hunt_l_patrol_progress: '순회 진행 판정', hunt_l_arrow_retry: '화살 재시도',
      hunt_l_qa_stop: '세션 종료', hunt_l_qa_pause: '플레이 일시정지',
      hunt_l_hunt_travel_top: '사냥터 복귀 이동 (전역)',
      hunt_b_npc_travel: 'NPC 이동', hunt_l_nt_scroll: '이동', hunt_l_nt_option: '대화 선택', hunt_l_nt_steps: '대화 단계',
      hunt_l_nt_npcs: 'NPC 이름', hunt_l_nt_target: '목표 영역', hunt_l_nt_map: '맵', hunt_l_box_l: '좌(L)',
      hunt_l_box_t: '상(T)', hunt_l_box_r: '우(R)', hunt_l_box_b: '하(B)',
      hunt_b_route: '경로 캡처', hunt_l_rc_url: 'URL', hunt_l_rc_token: '토큰', hunt_l_rc_rev: '소스 리비전',
      hunt_l_patrol_points_top: '전역 순회 좌표', hunt_t_patrol_points_top: '맵 행에 좌표가 없을 때 사용하는 기본 순회 좌표.',
      hunt_l_spell_mp: '스펠 MP 보정', hunt_l_mp: 'MP',
      hunt_l_point: '좌표', hunt_l_travel: '이동 방법', hunt_l_scroll_item: '두루마리 아이템',
      hunt_l_scroll_title: '두루마리 이름', hunt_l_menu_layout: '메뉴 레이아웃', hunt_l_section: '메뉴 섹션',
      hunt_l_destination: '목적지', hunt_l_town_map: '마을 맵', hunt_l_town_region: '마을 구역',
      hunt_l_teleport_cost: '텔레포트 비용', hunt_l_min_saving: '최소 절약', hunt_l_walk_radius: '도보 반경', hunt_l_merchant_point: '상인 지점',
      // units
      hunt_u_sec: '초', hunt_u_tile: '타일', hunt_u_pct: '%', hunt_u_cnt: '개', hunt_u_adena: '아데나',
      hunt_u_clv: '캐릭터 레벨', hunt_u_slot: '슬롯', hunt_u_hp: 'HP', hunt_u_mp: 'MP', hunt_u_spell: '스펠 ID',
      hunt_u_buff: '버프 ID', hunt_u_item: '아이템 코드', hunt_u_map: '맵 ID', hunt_u_region: '구역 ID',
      hunt_u_weight: '가중치', hunt_u_dps: 'HP/초', hunt_u_frac: '비율',
      // slot editor
      hunt_mode_custom: '이 슬롯 전용', hunt_mode_template: '프로필 사용', hunt_mode_default: '기본 프로필',
      hunt_hint_custom: '이 슬롯만의 전용 설정을 편집합니다.', hunt_hint_template: '선택한 프로필을 그대로 사용합니다.',
      hunt_hint_default: '내 기본 프로필을 상속합니다.',
      hunt_pick_template: '프로필', hunt_preview_note: '읽기 전용 미리보기입니다. 수정하려면 아래 버튼을 누르세요.',
      hunt_edit_here: '이 슬롯에서 직접 수정', hunt_no_template: '사용할 수 있는 프로필이 없습니다.',
      hunt_revert: '되돌리기', hunt_changes_n: '변경 {0}개', hunt_no_changes: '변경 사항 없음',
      hunt_errors_n: '오류 {0}개 · 첫 오류로 이동', hunt_pushed: '게임에 바로 적용됨',
      hunt_not_pushed: '게임이 꺼져 있어 다음 접속 때 적용됩니다.',
      hunt_switch_title: '전용 설정을 버릴까요?', hunt_switch_msg: '이 슬롯 전용 설정의 수정 내용이 사라집니다. 프로필로 전환하기 전에 새 프로필로 저장할 수 있습니다.',
      hunt_switch_discard: '버리고 전환', hunt_save_as_profile: '새 프로필로 저장',
      hunt_profile_name: '새 프로필 이름', hunt_profile_created: '프로필을 만들었습니다.',
      hunt_changed_title: '다른 곳에서 변경됨', hunt_changed_msg: '이 설정이 다른 곳에서 변경되었습니다. 최신 내용을 불러올까요? 지금 편집 중인 내용은 사라집니다.',
      hunt_reload: '새로 불러오기', hunt_load_fail: '설정을 불러오지 못했습니다.',
      hunt_slot_not_found: '슬롯을 찾을 수 없습니다.', hunt_back_machines: '← 머신',
      hunt_agent_online: '게임 실행 중', hunt_agent_offline: '게임 꺼짐', hunt_slot_page_title: '사냥 설정',
      hunt_default_chain: '내 기본 프로필 → 없으면 관리자 기본 설정',
      hunt_default_applied_user: "적용: 내 기본 프로필 '{0}'", hunt_default_applied_platform: "적용: 관리자 기본 설정 '{0}'",
      hunt_default_none_warn: '적용할 기본 프로필이 없습니다.',
      hunt_empty_warn: '이 슬롯은 적용할 사냥 설정이 없습니다 — 에이전트가 설정을 받지 못해 재접속을 반복할 수 있습니다. 전용 설정을 입력하거나 관리자 기본 설정을 요청하세요.',
      hunt_own_group: '내 프로필', hunt_shared_group: '공용 프로필'
    },
    'zh-CN': {
      hunt_title: '狩猎设置', hunt_search_ph: '搜索设置…', hunt_no_results: '没有搜索结果。',
      hunt_add_map: '添加狩猎地图', hunt_add_row: '+ 添加', hunt_remove: '删除',
      hunt_rows_empty: '暂无条目。用「+ 添加」新建，点击条目编辑。', hunt_row_blank: '（空条目）', hunt_row_incomplete: '请填写必填项。',
      hunt_points_n: '巡逻坐标 {0} 个', hunt_here: '添加当前位置', hunt_clear_points: '清除全部坐标',
      hunt_canvas_hint: '点击地图=添加坐标 · 拖动=移动 · 滚轮=缩放 · 拖动点可移动，× 删除',
      hunt_me: '我的位置', hunt_not_on_map: '角色不在该地图，无法使用当前位置。',
      hunt_map_pick_first: '请先选择地图。',
      hunt_travel_unset: '未设置', hunt_travel_summary: '卷轴 {0}',
      hunt_tag_ph: '输入后回车',
      hunt_err_num: '请输入数字。', hunt_err_int: '请输入整数。', hunt_err_min: '必须 ≥ {0}。',
      hunt_err_max: '必须 ≤ {0}。', hunt_err_json: 'JSON 格式不正确。',
      hunt_err_json_obj: '设置必须是对象（JSON）。', hunt_err_required: '此项必填。',
      hunt_json_apply: '应用到表单', hunt_json_refresh: '从表单载入',
      hunt_json_hint: '专家用。直接编辑全部设置，点击“应用到表单”会校验并写回表单。',
      hunt_json_applied: '已将 JSON 应用到表单。', hunt_key: '键',
      hunt_sec_ground: '狩猎场', hunt_sec_ground_d: '按等级段设置狩猎地图与巡逻坐标。',
      hunt_sec_combat: '战斗·目标', hunt_sec_combat_d: '设置攻击对象、优先级、风筝与箭矢。',
      hunt_sec_party: '组队', hunt_sec_party_d: '同一账号的角色之间集火、跟随与拾取分工。按名字列表工作，与游戏内组队无关。',
      hunt_l_party_leader: '队长角色名', hunt_t_party: '优先攻击队长正在打的怪，并在队长身边（3 格）等待；队长不在画面内时向服务器查询位置，在同一地图的猎区内才跟随。',
      hunt_l_party_members: '队员', hunt_t_party_members: '队员名字（含队长）。队员正在打的怪不视为被抢占，一起打攻击队员的怪。队员以我为目标（治疗）不视为 PK（真正被打仍算 PK）。',
      hunt_l_party_loot: '拾取担当', hunt_t_party_loot: '该角色在画面内时其他队员不拾取（不在画面内则各自拾取）。留空则各自拾取。',
      hunt_l_party_follow: '跟随队长', hunt_l_party_focus: '集火（优先队员目标）',
      hunt_b_stability: '长期运行', hunt_l_sched_enabled: '运行·休息时间表',
      hunt_l_sched_run: '运行', hunt_l_sched_rest: '休息', hunt_u_min: '分钟',
      hunt_t_schedule: '循环运行 N 分钟 ↔ 休息 M 分钟（每个角色的开始时间略有差异）。休息期间在村庄等待。',
      hunt_l_ds_enabled: '连续死亡保护', hunt_l_ds_count: '连续死亡判定', hunt_l_ds_rest: '连续死亡休息',
      hunt_t_death_streak: '开启后，1 小时内死亡达到该次数就在村庄休息该时长。断线画面不计为死亡，作业重启后记录清零。',
      hunt_b_reposition: '停滞换位', hunt_l_rp_income: '无收入', hunt_l_rp_position: '原地打转',
      hunt_l_rp_nomob: '无怪',
      hunt_t_reposition: '在猎区内 ① 收入（经验·掉落·金币）在该时间内没有增长，或 ② 在 6 格范围内打转该时间且没有收入，或 ③ 该时间内看不到怪时，先走到巡逻点·击杀地点中最远的位置，而不是回城。往返村庄和移动的时间不计入。',
      hunt_sec_survival: '生存', hunt_sec_survival_d: '药水·治疗·返回·回避等生存规则。',
      hunt_sec_buffs: '增益', hunt_sec_buffs_d: '保持的增益、地图增益、活动增益、加速商店。',
      hunt_sec_restock: '返回·补给', hunt_sec_restock_d: '返回卷轴、购买/出售、返回后行为、仓库、进食、魔法书。',
      hunt_sec_explore: '探索·战利品', hunt_sec_explore_d: '地图探索与战利品规则。',
      hunt_sec_advanced: '高级', hunt_sec_advanced_d: '计时器、路径记录、少用的传送与设置。标签旁灰色字即实际设置键。',
      hunt_sec_json: 'JSON', hunt_sec_json_d: '直接编辑全部设置（专家用）。',
      hunt_l_hunt_maps: '按等级段的狩猎地图', hunt_t_hunt_maps: '角色等级进入区间后在该地图狩猎。坐标留空则从到达点开始探索。',
      hunt_l_patrol_radius: '巡逻点到达半径', hunt_t_patrol_radius: '进入该半径即切换到下一巡逻点（0=精确到达）。',
      hunt_l_lv_min: '等级下限', hunt_l_lv_max: '等级上限',
      hunt_l_map: '地图', hunt_l_region: '区域', hunt_l_entry: '入口·避让地块',
      hunt_l_entry_map: '传送门地图', hunt_l_entry_x: '入口 X', hunt_l_entry_y: '入口 Y',
      hunt_l_push_x: '进入点击 X', hunt_l_push_y: '进入点击 Y',
      hunt_l_avoid_tiles: '避让地块', hunt_l_x: 'X', hunt_l_y: 'Y', hunt_l_approach: '接近途经点',
      hunt_l_allow_mobs: '允许攻击的怪', hunt_t_allow_mobs: '留空=攻击所有怪；添加后只攻击指定的怪。',
      hunt_l_ignore_mobs: '忽略的怪（等级段）', hunt_t_ignore_mobs: '仅在指定角色等级段忽略，之后照常攻击。',
      hunt_l_never_target: '始终排除的怪', hunt_t_never_target: '任何情况下都不攻击。',
      hunt_l_priority: '怪物优先级', hunt_t_priority: '权重越高越优先（默认 1）。≥2 倍会切换当前目标。',
      hunt_l_stick: '锁定攻击过的怪', hunt_t_stick: '直到目标死亡、离开或出现更高优先级的怪为止，持续攻击该怪。',
      hunt_l_attack_others: '也攻击其他玩家正在打的怪', hunt_t_attack_others: '关闭（默认）时跳过其他玩家·宠物·召唤物正在瞄准或 5 秒内打过的怪。队员正在打的怪例外，一起打。',
      hunt_l_seek_progress: '追怪进度判定', hunt_t_seek_progress: '追赶射程外的怪时，若在该时间内没有接近，则 20 秒内跳过该怪。',
      hunt_b_retaliate: '反击（被忽略怪攻击时）', hunt_t_retaliate: '忽略列表中的怪攻击我时，不再忽略而延后击杀（权重随时间上升）。',
      hunt_l_retaliate_mobs: '反击对象（忽略怪）', hunt_l_retaliate_base: '反击初始权重', hunt_l_retaliate_step: '权重增量', hunt_l_retaliate_step_sec: '增加间隔',
      hunt_l_attack_in_place: '原地攻击', hunt_l_attack_hold: '保持攻击姿势', hunt_l_bow_range: '弓射程',
      hunt_l_kite_en: '使用风筝（弓）', hunt_l_kite_skip: '风筝排除（站立射击）',
      hunt_l_arrow_priority: '普通箭矢优先级', hunt_l_undead_arrow: '不死箭矢优先级',
      hunt_b_kite_tune: '风筝细调（高级）',
      hunt_l_kite_dps: '基础 DPS', hunt_l_kite_ttk: '基础击杀时间', hunt_l_kite_reserve: '逃脱预留 HP',
      hunt_l_kite_pbudget: '药水预算', hunt_l_kite_php: '每瓶恢复', hunt_l_kite_threat: '威胁半径',
      hunt_l_kite_sbudget: '治疗魔法预算', hunt_l_kite_shp: '每次魔法恢复',
      hunt_l_kite_gap: '射击间隔', hunt_l_kite_lag: '延迟余量', hunt_l_kite_mingain: '最小收益',
      hunt_l_kite_cycle: '射击周期', hunt_l_kite_lock: '射击锁定', hunt_l_kite_stun: '受击硬直',
      hunt_l_kite_shootmin: '最小射击距离', hunt_l_kite_orc: '追击者步速', hunt_l_kite_walk: '自身步速',
      hunt_l_kite_latency: '网络延迟', hunt_l_kite_rmax: '最大后退', hunt_l_kite_rmin: '最小后退',
      hunt_l_kite_race: '竞速逃脱时间', hunt_l_kite_aggr: '激进',
      hunt_l_kite_levels: '按怪击杀难度', hunt_l_kite_aggain: '激进加权', hunt_l_kite_open: '开阔地加权',
      hunt_l_kite_los: '视线加权', hunt_l_kite_leasht: '自掉落距离', hunt_l_kite_leashw: '掉落加权',
      hunt_l_kite_contactd: '接触距离', hunt_l_kite_contacth: '接触受击时间', hunt_l_kite_outpace: '超越收益',
      hunt_l_kite_pincer: '夹击余量', hunt_l_kite_pincere: '夹击逃脱', hunt_l_kite_diseng: '接触脱离',
      hunt_l_kite_arrival: '到达收尾', hunt_l_kite_rfinish: '竞速收尾', hunt_l_kite_failretreat: '后退失败宽限',
      hunt_l_kite_corner: '被逼反击',
      hunt_l_heal_hp: '用药 HP', hunt_t_heal_hp: '低于该 HP% 时使用恢复药（留空则不使用药水治疗）。',
      hunt_l_heal_interval: '用药间隔', hunt_l_heal_codes: '恢复药（优先级顺序）',
      hunt_l_heal_spells: '治疗魔法', hunt_l_spell: '法术', hunt_l_hp_pct: 'HP%',
      hunt_l_return: '返回条件（满足任一）', hunt_l_cond_type: '条件', hunt_l_item: '物品',
      hunt_l_qty: '数量', hunt_l_cond_value: '数值',
      hunt_opt_item_below: '物品不足 N 个', hunt_opt_hp_below: 'HP 低于 %', hunt_opt_weight_above: '负重 % 以上',
      hunt_l_reserve_hits: '受击预备倍数', hunt_t_reserve_hits: '当 HP ≤ 最近受击 × N 时返回（0=不使用）。',
      hunt_l_avoid_radius: '危险怪回避半径', hunt_t_avoid_radius: '0~8，0=关闭。',
      hunt_l_avoid_mobs: '回避的怪', hunt_t_avoid_mobs: '名称部分匹配即视为危险，进入半径则返回。',
      hunt_l_return_codes: '返回卷轴', hunt_t_return_codes: '没有返回卷轴时使用。',
      hunt_b_damage_escape: '受击逃脱', hunt_l_trigger_mobs: '触发怪', hunt_l_defend_hp: '防守 HP%',
      hunt_l_fallback_travel: '备用传送', hunt_l_cure_items: '解毒（物品·法术）',
      hunt_l_item_codes: '物品', hunt_l_interval: '间隔',
      hunt_t_cure_items: '身上出现触发状态时立即解除：默认先用持有的物品，没有则使用解毒法术。',
      hunt_l_cure_trigger: '触发状态（减益）', hunt_t_cure_trigger: '出现在增益列表中的状态 ID（5319 = 中毒），不是要施放的增益。',
      hunt_l_cure_spells: '解毒法术', hunt_l_cure_prefer: '优先法术（节省药水）',
      hunt_t_spell_auto: '未学会的法术会被忽略；已学会但不在快捷栏的法术自动登记到 F5–F12 空位。',
      hunt_l_mi_buffs: '持续中判断增益', hunt_t_mi_buffs: '列出的增益存在时不使用物品（为空时根据使用后新出现的增益自动学习）。',
      hunt_t_eb_scroll_slot: '留空时卷轴使用已登记的快捷栏位置，或自动登记到空位。',
      hunt_l_maintain_buffs: '保持的增益', hunt_l_buffs_by_map: '地图额外增益',
      hunt_l_buff_recast: '增益重施间隔', hunt_l_maintain_items: '维持物品（增益药等）', hunt_l_mp_below: 'MP% 以下',
      hunt_b_event_buff: '活动增益', hunt_l_eb_enabled: '自动补充活动增益', hunt_l_eb_buffids: '增益',
      hunt_l_eb_object: '增益对象', hunt_l_eb_scroll_code: '卷轴物品', hunt_l_eb_scroll_slot: '卷轴快捷栏',
      hunt_l_eb_scroll_title: '卷轴名称', hunt_l_eb_section: '菜单区段', hunt_l_eb_dest: '目的地',
      hunt_l_eb_town_map: '城镇地图', hunt_l_eb_town_region: '城镇区域', hunt_l_eb_layout: '菜单布局',
      hunt_l_eb_wh: '仓库坐标', hunt_l_eb_obj_pt: '对象坐标', hunt_l_eb_resume: '恢复坐标',
      hunt_l_eb_resume_map: '恢复地图', hunt_l_eb_hunt_travel: '返回狩猎场传送',
      hunt_b_haste: '加速商店', hunt_l_haste_en: '启用', hunt_l_haste_names: '商人名称',
      hunt_l_haste_price: '价格', hunt_l_haste_radius: '搜索半径', hunt_l_haste_buff: '增益 ID',
      hunt_l_haste_retry: '重试间隔', hunt_l_haste_full: '充足阈值', hunt_l_haste_timeout: '超时',
      hunt_b_transform: '变身卷轴', hunt_l_tf_en: '启用', hunt_l_tf_items: '变身卷轴', hunt_t_tf_items: '没有变身增益时阅读此卷轴。',
      hunt_l_tf_entry: '变身列表项', hunt_t_tf_entry: '变身窗口中要点击的项目文字', hunt_l_tf_buffs: '变身增益 ID', hunt_l_tf_duration: '无增益时的重用间隔',
      hunt_b_npc_contract: 'NPC 契约·结算', hunt_l_nc_en: '启用', hunt_t_nc_en: '需要填写 NPC 名称才会运行。', hunt_l_nc_npc: '契约 NPC',
      hunt_l_nc_point: 'NPC 旁坐标', hunt_l_nc_scroll: '前往 NPC 村庄', hunt_l_nc_contract: '契约对话项', hunt_l_nc_settle: '结算对话项', hunt_l_nc_renew: '续约对话项',
      hunt_l_nc_items: '结算物品', hunt_l_nc_values: '物品价值', hunt_l_nc_settle_value: '开始结算的价值', hunt_l_nc_kill_names: '契约怪物（名称片段）',
      hunt_t_nc_kill_names: '多次击杀此怪物却没有物品时视为没有契约并重新签约。', hunt_l_nc_kills: '确认契约的击杀数',
      hunt_l_hold_town: '城镇待机（不开始狩猎）', hunt_t_hold_town: '启用后返回城镇停留，出售·仓库·购买照常进行。',
      hunt_l_world_fps: '世界采样数（每秒）', hunt_t_world_fps: '发送给网页地图的周围位置采样数 0~30',
      hunt_l_kite_names: '仅风筝这些（空=全部）', hunt_t_kite_names: '填写后仅对这些怪物保持距离射击，其余站立射击。',
      hunt_l_loot_kill: '每只击杀预计', hunt_t_loot_kill: '用于判断能否在我的掉落保护时间内先结束战斗。', hunt_l_loot_protect: '我的掉落保护时间',
      hunt_l_wh_npcs: '仓库管理员名称（多个）', hunt_l_wh_withdraw_menu: '取出菜单文字', hunt_l_wh_travel: '前往仓库（卷轴）',
      hunt_l_wh_withdraw_codes: '一次性取出的物品', hunt_l_wh_withdraw_token: '取出令牌', hunt_t_wh_withdraw_token: '更换令牌后会再次从仓库取出上述全部物品。',
      hunt_l_wh_withdraw_names: '取出物品名（精确）', hunt_t_wh_withdraw_names: '只取出仓库列表文字与该名称完全相同的行（比较时去掉 +0 前缀和堆叠数量）。上面的物品至少要有一个，取出才会执行。',
      hunt_l_wh_keep: '背包保留数量', hunt_t_wh_keep: '指定物品合计只在背包保留该数量，其余每次到访时不论负重都寄存。',
      hunt_l_sell_merchant: '出售商人', hunt_l_trip_min: '出行最少数量', hunt_l_sell_point: '商人旁坐标', hunt_l_sell_travel: '前往出售城镇',
      hunt_l_haste_hold: '暂停（调试）', hunt_l_haste_travel: '传送',
      hunt_l_haste_force: '立即强制购买 1 次', hunt_t_haste_force: '开启并保存后，即使已有加速也会交易一次。保持开启再次保存会再执行一次，用完请关闭。',
      hunt_b_return_scroll: '返回卷轴', hunt_l_return_scroll: '返回卷轴',
      hunt_b_restock: '返回时补给', hunt_l_restock_buy: '购买（至目标数量）', hunt_l_restock_sell: '出售',
      hunt_l_target_qty: '目标数量', hunt_l_minimum_qty: '补货阈值', hunt_l_min_level: '最低等级',
      hunt_l_depart_min: '出发最少数量', hunt_t_depart_min: '低于此数量时不出发狩猎（如箭矢），在城镇等待补齐并在网页提示；留空则买得起多少买多少后继续狩猎。',
      hunt_l_restock_merchant: '购买 NPC', hunt_l_sell_merchants: '出售 NPC（按顺序）',
      hunt_l_weight_limit: '负重限制', hunt_t_weight_limit: '到达该负重% 时停止购买。',
      hunt_l_restock_travel: '商店传送（必填）', hunt_l_hunt_travel: '返回狩猎场传送',
      hunt_badv_buy: '购买细节', hunt_l_min_adena: '最低金币', hunt_l_reserve_adena: '预留金币',
      hunt_l_available: '持有计入代码', hunt_l_equipped: '装备计入代码', hunt_l_purchase_qty: '批量购买数量',
      hunt_l_auto_target: '自动目标量', hunt_l_max_qty: '最大数量', hunt_l_learned_spell: '学习法术',
      hunt_l_pack_size: '每包数量', hunt_l_register_slot: '注册快捷栏', hunt_l_equip: '装备', hunt_l_buy_name: '商店标识',
      hunt_l_buy_merchant: '购买 NPC', hunt_l_buy_travel: '传送',
      hunt_b_after_return: '返回后', hunt_l_town_wait: '城镇等待', hunt_l_resume_hunt: '返回狩猎场',
      hunt_l_recover_hp: '恢复 HP%', hunt_l_ar_map: '安全地图', hunt_l_ar_region: '安全区域', hunt_l_ar_point: '返回地点（必填）',
      hunt_b_warehouse: '仓库寄存', hunt_l_wh_en: '使用仓库', hunt_l_wh_items: '寄存物品',
      hunt_l_wh_preserve: '保留物品', hunt_l_wh_always: '每次寄存', hunt_l_wh_weight: '起始负重%',
      hunt_l_wh_target: '目标负重%', hunt_l_wh_section: '菜单区段', hunt_l_wh_deposit: '寄存菜单', hunt_l_wh_npc: '仓库 NPC',
      hunt_b_food: '自动进食', hunt_l_food_en: '饱食度管理', hunt_l_food_target: '目标 %', hunt_l_food_min: '起始 %',
      hunt_l_food_interval: '间隔', hunt_l_food_codes: '食物物品',
      hunt_b_spell_shop: '魔法书商店', hunt_l_ss_buy: '购买', hunt_l_ss_sell: '出售', hunt_l_ss_merchant: '购买 NPC',
      hunt_l_ss_travel: '商店传送', hunt_l_ss_hunt_travel: '返回狩猎场传送',
      hunt_l_learn_books: '魔法书学习', hunt_t_learn_books: '从背包读书学习法术（每等级一次）。',
      hunt_b_exploration: '探索', hunt_l_exp_en: '启用探索', hunt_l_exp_exclude: '排除区域',
      hunt_l_exp_margin: '边界余量', hunt_l_exp_revisit: '重访间隔', hunt_l_exp_memory: '记忆时间',
      hunt_l_exp_idle: '空闲后进入', hunt_l_exp_progress: '进度判定', hunt_l_exp_unreach: '不可达判定',
      hunt_b_loot: '战利品', hunt_l_loot_others: '也拾取他人掉落', hunt_t_loot_others: '关闭时只拾取自己的掉落。', hunt_l_loot_scav: '拾取装备',
      hunt_l_loot_weight: '负重限制', hunt_l_loot_own: '自掉落优先级', hunt_l_loot_exclude: '排除物品名',
      hunt_l_loot_memory: '记忆时间', hunt_l_loot_view: '侦测距离', hunt_l_loot_return: '返回距离',
      hunt_l_loot_near: '即时拾取距离', hunt_l_loot_clear: '战斗优先半径',
      hunt_l_walk_stall: '步行停滞判定', hunt_l_patrol_progress: '巡逻进度判定', hunt_l_arrow_retry: '箭矢重试',
      hunt_l_qa_stop: '结束会话', hunt_l_qa_pause: '暂停游玩',
      hunt_l_hunt_travel_top: '返回狩猎场传送（全局）',
      hunt_b_npc_travel: 'NPC 传送', hunt_l_nt_scroll: '传送', hunt_l_nt_option: '对话选项', hunt_l_nt_steps: '对话步骤',
      hunt_l_nt_npcs: 'NPC 名称', hunt_l_nt_target: '目标区域', hunt_l_nt_map: '地图', hunt_l_box_l: '左(L)',
      hunt_l_box_t: '上(T)', hunt_l_box_r: '右(R)', hunt_l_box_b: '下(B)',
      hunt_b_route: '路径记录', hunt_l_rc_url: 'URL', hunt_l_rc_token: '令牌', hunt_l_rc_rev: '源版本',
      hunt_l_patrol_points_top: '全局巡逻坐标', hunt_t_patrol_points_top: '地图行无坐标时使用的默认巡逻坐标。',
      hunt_l_spell_mp: '法术 MP 修正', hunt_l_mp: 'MP',
      hunt_l_point: '坐标', hunt_l_travel: '移动方式', hunt_l_scroll_item: '卷轴物品',
      hunt_l_scroll_title: '卷轴名称', hunt_l_menu_layout: '菜单布局', hunt_l_section: '菜单区段',
      hunt_l_destination: '目的地', hunt_l_town_map: '城镇地图', hunt_l_town_region: '城镇区域',
      hunt_l_teleport_cost: '传送成本', hunt_l_min_saving: '最小节省', hunt_l_walk_radius: '步行半径', hunt_l_merchant_point: '商人坐标',
      hunt_u_sec: '秒', hunt_u_tile: '格', hunt_u_pct: '%', hunt_u_cnt: '个', hunt_u_adena: '金币',
      hunt_u_clv: '角色等级', hunt_u_slot: '栏位', hunt_u_hp: 'HP', hunt_u_mp: 'MP', hunt_u_spell: '法术ID',
      hunt_u_buff: '增益ID', hunt_u_item: '物品代码', hunt_u_map: '地图ID', hunt_u_region: '区域ID',
      hunt_u_weight: '权重', hunt_u_dps: 'HP/秒', hunt_u_frac: '比例',
      hunt_mode_custom: '此栏位专用', hunt_mode_template: '使用方案', hunt_mode_default: '默认方案',
      hunt_hint_custom: '编辑该栏位的专用设置。', hunt_hint_template: '直接使用所选方案。',
      hunt_hint_default: '继承我的默认方案。',
      hunt_pick_template: '方案', hunt_preview_note: '只读预览。如需修改请点击下方按钮。',
      hunt_edit_here: '在此栏位直接修改', hunt_no_template: '没有可用的方案。',
      hunt_revert: '撤销', hunt_changes_n: '{0} 处更改', hunt_no_changes: '没有更改',
      hunt_errors_n: '{0} 个错误 · 跳到第一个', hunt_pushed: '已立即应用到游戏',
      hunt_not_pushed: '游戏已关闭，将在下次登录时应用。',
      hunt_switch_title: '放弃专用设置？', hunt_switch_msg: '该栏位专用设置的修改将丢失。切换到方案前可另存为新方案。',
      hunt_switch_discard: '放弃并切换', hunt_save_as_profile: '另存为新方案',
      hunt_profile_name: '新方案名称', hunt_profile_created: '已创建方案。',
      hunt_changed_title: '已在别处更改', hunt_changed_msg: '该设置已在别处更改。要载入最新内容吗？当前正在编辑的内容会丢失。',
      hunt_reload: '重新载入', hunt_load_fail: '无法载入设置。',
      hunt_slot_not_found: '找不到栏位。', hunt_back_machines: '← 机器',
      hunt_agent_online: '游戏运行中', hunt_agent_offline: '游戏已关闭', hunt_slot_page_title: '狩猎设置',
      hunt_default_chain: '我的默认方案 → 若无则管理员默认设置',
      hunt_default_applied_user: "已应用：我的默认方案 '{0}'", hunt_default_applied_platform: "已应用：管理员默认设置 '{0}'",
      hunt_default_none_warn: '没有可应用的默认方案。',
      hunt_empty_warn: '此栏位没有可应用的狩猎设置 —— 代理将收不到设置并可能反复重连。请填写专用设置或申请管理员默认设置。',
      hunt_own_group: '我的方案', hunt_shared_group: '公用方案'
    }
  });

  // ── chips (values/labels/decor come from the shared App.picker catalog widget) ──
  const P = App.picker;
  function chipHtml(kind, v) {
    const decor = kind === 'tag' ? '' : P.decor(kind, v, 22);
    const label = kind === 'tag' ? String(v) : (P.label(kind, v) || String(v));
    return `<span class="chip" data-v="${esc(v)}">${decor}<span class="hf-chip-lab">${esc(label)}</span><button type="button" class="hf-chipdel" aria-label="${esc(T('hunt_remove'))}">✕</button></span>`;
  }
  function chipValues(fieldEl, kind) {
    return [...fieldEl.querySelectorAll('.hf-chips > .chip')].map(c => kind === 'tag' ? c.dataset.v : P.cast(kind, c.dataset.v)).filter(v => v !== undefined && v !== '');
  }

  // ── field constructors ──
  const F = (type, path, l, o) => Object.assign({ type, path, l }, o || {});   // top-level (dotted path)
  const S = (type, k, l, o) => Object.assign({ type, k, l }, o || {});         // sub-field (relative key)
  const xy = () => [S('num', 'x', 'hunt_l_x', { int: true }), S('num', 'y', 'hunt_l_y', { int: true })];
  const xyMap = () => [S('num', 'x', 'hunt_l_x', { int: true }), S('num', 'y', 'hunt_l_y', { int: true }), S('picker', 'map_id', 'hunt_l_eb_resume_map', { kind: 'map' })];
  function travelFields(variant) {
    const f = [
      S('picker', 'scroll_item_code', 'hunt_l_scroll_item', { kind: 'item' }),
      S('text', 'scroll_title', 'hunt_l_scroll_title'),
      S('group', 'point', 'hunt_l_point', { fields: xy() }),
      S('text', 'destination', 'hunt_l_destination'),
      S('text', 'section', 'hunt_l_section'),
      S('picker', 'town_map', 'hunt_l_town_map', { kind: 'map' }),
      S('picker', 'town_region', 'hunt_l_town_region', { kind: 'regionName' }),
      S('text', 'menu_layout', 'hunt_l_menu_layout', { ph: 'TalkingScrollLayout' })
    ];
    if (variant === 'hunt') f.push(S('num', 'teleport_cost_tiles', 'hunt_l_teleport_cost', { unit: 'hunt_u_tile', int: true }), S('num', 'min_saving_tiles', 'hunt_l_min_saving', { unit: 'hunt_u_tile', int: true }));
    if (variant === 'shop') f.push(S('num', 'walk_radius', 'hunt_l_walk_radius', { unit: 'hunt_u_tile', int: true, ph: '20' }), S('group', 'merchant_point', 'hunt_l_merchant_point', { fields: xy() }));
    return f;
  }
  // Sell rows: the item, and optionally the merchant and town for a dedicated sale trip (Lua remote sale:
  // a town's rows held at least trip_min times start the trip; point = the tile beside that merchant).
  const sellFields = () => ([
    S('picker', 'item_code', 'hunt_l_item', { kind: 'item' }),
    S('picker', 'merchant', 'hunt_l_sell_merchant', { kind: 'npc' }),
    S('num', 'trip_min', 'hunt_l_trip_min', { int: true, min: 1, ph: '1' }),
    S('group', 'point', 'hunt_l_sell_point', { fields: xy() }),
    S('travel', 'travel', 'hunt_l_sell_travel', { variant: 'shop' })
  ]);
  // depart: the restock rows also take depart_min_qty (the least stock a hunt starts with; Lua holds the departure
  // below it and reports the shortage).
  const buyFields = (adv, depart) => ([
    S('picker', 'item_code', 'hunt_l_item', { kind: 'item' }),
    S('num', 'target_qty', 'hunt_l_target_qty', { int: true }),
    S('num', 'minimum_qty', 'hunt_l_minimum_qty', { int: true, ph: '1' })
  ].concat(depart ? [S('num', 'depart_min_qty', 'hunt_l_depart_min', { int: true, min: 1, hint: 'hunt_t_depart_min' })] : [], [
    S('num', 'min_level', 'hunt_l_min_level', { unit: 'hunt_u_clv', int: true })
  ]).concat(adv ? [{ adv: true, fields: [
    S('num', 'min_adena', 'hunt_l_min_adena', { unit: 'hunt_u_adena', int: true }),
    S('num', 'reserve_adena', 'hunt_l_reserve_adena', { unit: 'hunt_u_adena', int: true, ph: '0' }),
    S('num', 'purchase_qty', 'hunt_l_purchase_qty', { int: true }),
    S('num', 'max_qty', 'hunt_l_max_qty', { int: true, ph: '10' }),
    S('num', 'pack_size', 'hunt_l_pack_size', { int: true, ph: '1' }),
    S('chips', 'available_codes', 'hunt_l_available', { kind: 'item' }),
    S('chips', 'equipped_codes', 'hunt_l_equipped', { kind: 'item' }),
    S('picker', 'learned_spell_id', 'hunt_l_learned_spell', { kind: 'skill' }),
    S('bool', 'auto_target', 'hunt_l_auto_target', { def: false }),
    S('bool', 'register_slot', 'hunt_l_register_slot', { def: false }),
    S('bool', 'equip', 'hunt_l_equip', { def: false }),
    S('picker', 'name', 'hunt_l_buy_name', { kind: 'itemName' }),
    S('picker', 'merchant', 'hunt_l_buy_merchant', { kind: 'npc' }),
    S('travel', 'travel', 'hunt_l_buy_travel', { variant: 'shop' })
  ] }] : []));

  // ── schema ──
  const SECTIONS = [
    { id: 'ground', icon: '🗺️', l: 'hunt_sec_ground', d: 'hunt_sec_ground_d', blocks: [
      { fields: [
        F('huntMaps', 'hunt_maps', 'hunt_l_hunt_maps', { hint: 'hunt_t_hunt_maps' }),
        F('num', 'patrol_arrive_radius', 'hunt_l_patrol_radius', { unit: 'hunt_u_tile', int: true, min: 0, ph: '2', hint: 'hunt_t_patrol_radius' })
      ] },
      { l: 'hunt_b_reposition', collapsible: true, fields: [
        F('num', 'patrol.reposition.income_seconds', 'hunt_l_rp_income', { unit: 'hunt_u_sec', int: true, ph: '300', hint: 'hunt_t_reposition' }),
        F('num', 'patrol.reposition.position_seconds', 'hunt_l_rp_position', { unit: 'hunt_u_sec', int: true, ph: '180' }),
        F('num', 'patrol.reposition.no_monster_seconds', 'hunt_l_rp_nomob', { unit: 'hunt_u_sec', int: true, ph: '120' })
      ] }
    ] },
    { id: 'combat', icon: '⚔️', l: 'hunt_sec_combat', d: 'hunt_sec_combat_d', blocks: [
      { fields: [
        F('chips', 'target.allow_mobs', 'hunt_l_allow_mobs', { kind: 'monster', hint: 'hunt_t_allow_mobs' }),
        F('chips', 'target.never_target', 'hunt_l_never_target', { kind: 'monster', hint: 'hunt_t_never_target' })
      ] },
      { fields: [
        F('rows', 'target.ignore_mobs', 'hunt_l_ignore_mobs', { hint: 'hunt_t_ignore_mobs', keep: r => !!r.name, fields: [
          S('picker', 'name', 'hunt_l_item', { kind: 'monster' }),
          S('num', 'min_level', 'hunt_l_lv_min', { unit: 'hunt_u_clv', int: true }),
          S('num', 'max_level', 'hunt_l_lv_max', { unit: 'hunt_u_clv', int: true })
        ] }),
        F('map', 'target.priority', 'hunt_l_priority', { kind: 'monster', vUnit: 'hunt_u_weight', vPh: '1', hint: 'hunt_t_priority' })
      ] },
      { l: 'hunt_b_retaliate', collapsible: true, fields: [
        F('chips', 'target.retaliate_mobs', 'hunt_l_retaliate_mobs', { kind: 'monster', hint: 'hunt_t_retaliate' }),
        F('num', 'target.retaliate_base_weight', 'hunt_l_retaliate_base', { unit: 'hunt_u_weight', step: 'any', ph: '0.5' }),
        F('num', 'target.retaliate_step', 'hunt_l_retaliate_step', { unit: 'hunt_u_weight', step: 'any', ph: '1' }),
        F('num', 'target.retaliate_step_seconds', 'hunt_l_retaliate_step_sec', { unit: 'hunt_u_sec', step: 'any', ph: '10' })
      ] },
      { fields: [
        F('bool', 'combat.attack_in_place', 'hunt_l_attack_in_place', { def: true }),
        F('bool', 'combat.attack_hold', 'hunt_l_attack_hold', { def: true }),
        F('bool', 'target.stick_until_dead', 'hunt_l_stick', { def: false, hint: 'hunt_t_stick' }),
        F('bool', 'target.attack_others_targets', 'hunt_l_attack_others', { def: false, hint: 'hunt_t_attack_others' }),
        F('num', 'combat.bow_range', 'hunt_l_bow_range', { unit: 'hunt_u_tile', int: true, ph: '12' }),
        F('bool', 'combat.kite.enabled', 'hunt_l_kite_en', { def: true }),
        F('chips', 'combat.kite.skip_names', 'hunt_l_kite_skip', { kind: 'monster' }),
        F('chips', 'combat.kite.names', 'hunt_l_kite_names', { kind: 'monster', hint: 'hunt_t_kite_names' }),
        F('chips', 'arrow_priority', 'hunt_l_arrow_priority', { kind: 'item' }),
        F('chips', 'undead_arrow_priority', 'hunt_l_undead_arrow', { kind: 'item' })
      ] },
      { l: 'hunt_b_kite_tune', collapsible: true, showKey: true, fields: [
        F('num', 'combat.kite.default_dps', 'hunt_l_kite_dps', { unit: 'hunt_u_dps', step: 'any', ph: '2.5' }),
        F('num', 'combat.kite.default_ttk', 'hunt_l_kite_ttk', { unit: 'hunt_u_sec', step: 'any', ph: '15' }),
        F('num', 'combat.kite.escape_reserve_percent', 'hunt_l_kite_reserve', { unit: 'hunt_u_pct', ph: '20' }),
        F('num', 'combat.kite.potion_budget', 'hunt_l_kite_pbudget', { unit: 'hunt_u_cnt', int: true, ph: '5' }),
        F('num', 'combat.kite.potion_hp', 'hunt_l_kite_php', { unit: 'hunt_u_hp', ph: '6' }),
        F('num', 'combat.kite.spell_budget', 'hunt_l_kite_sbudget', { unit: 'hunt_u_cnt', int: true, ph: '4' }),
        F('num', 'combat.kite.spell_hp', 'hunt_l_kite_shp', { unit: 'hunt_u_hp', ph: '15' }),
        F('num', 'combat.kite.threat_radius', 'hunt_l_kite_threat', { unit: 'hunt_u_tile', int: true, ph: '8' }),
        F('num', 'combat.kite.gap', 'hunt_l_kite_gap', { unit: 'hunt_u_tile', step: 'any', ph: '2' }),
        F('num', 'combat.kite.lag_margin', 'hunt_l_kite_lag', { unit: 'hunt_u_tile', step: 'any', ph: '1' }),
        F('num', 'combat.kite.min_gain', 'hunt_l_kite_mingain', { unit: 'hunt_u_frac', step: 'any', ph: '0.05' }),
        F('num', 'combat.kite.shot_cycle', 'hunt_l_kite_cycle', { unit: 'hunt_u_sec', step: 'any' }),
        F('num', 'combat.kite.shot_lock', 'hunt_l_kite_lock', { unit: 'hunt_u_sec', step: 'any' }),
        F('num', 'combat.kite.hit_stun', 'hunt_l_kite_stun', { unit: 'hunt_u_sec', step: 'any' }),
        F('num', 'combat.kite.shoot_min', 'hunt_l_kite_shootmin', { unit: 'hunt_u_tile', step: 'any' }),
        F('num', 'combat.kite.orc_step', 'hunt_l_kite_orc', { unit: 'hunt_u_sec', step: 'any', ph: '0.8' }),
        F('num', 'combat.kite.walk_step', 'hunt_l_kite_walk', { unit: 'hunt_u_sec', step: 'any' }),
        F('num', 'combat.kite.latency', 'hunt_l_kite_latency', { unit: 'hunt_u_sec', step: 'any', ph: '0.3' }),
        F('num', 'combat.kite.retreat_max', 'hunt_l_kite_rmax', { unit: 'hunt_u_tile', int: true, ph: '5' }),
        F('num', 'combat.kite.retreat_min', 'hunt_l_kite_rmin', { unit: 'hunt_u_tile', int: true }),
        F('num', 'combat.kite.race_escape_seconds', 'hunt_l_kite_race', { unit: 'hunt_u_sec', step: 'any', ph: '5' }),
        F('bool', 'combat.kite.aggressive', 'hunt_l_kite_aggr', { def: true }),
        F('map', 'combat.kite.mob_levels', 'hunt_l_kite_levels', { kind: 'monster', vUnit: 'hunt_u_clv', vInt: true }),
        F('num', 'combat.kite.aggressive_gain', 'hunt_l_kite_aggain', { unit: 'hunt_u_frac', step: 'any', ph: '0.15' }),
        F('num', 'combat.kite.open_weight', 'hunt_l_kite_open', { unit: 'hunt_u_weight', step: 'any', ph: '2' }),
        F('num', 'combat.kite.los_weight', 'hunt_l_kite_los', { unit: 'hunt_u_weight', step: 'any', ph: '1' }),
        F('num', 'combat.kite.leash_tiles', 'hunt_l_kite_leasht', { unit: 'hunt_u_tile', int: true, ph: '8' }),
        F('num', 'combat.kite.leash_weight', 'hunt_l_kite_leashw', { unit: 'hunt_u_weight', step: 'any', ph: '1' }),
        F('num', 'combat.kite.contact_distance', 'hunt_l_kite_contactd', { unit: 'hunt_u_tile', step: 'any', ph: '2' }),
        F('num', 'combat.kite.contact_hit_seconds', 'hunt_l_kite_contacth', { unit: 'hunt_u_sec', step: 'any', ph: '1' }),
        F('num', 'combat.kite.escape_outpace_gain', 'hunt_l_kite_outpace', { unit: 'hunt_u_frac', step: 'any', ph: '0.4' }),
        F('num', 'combat.kite.pincer_spare_fraction', 'hunt_l_kite_pincer', { unit: 'hunt_u_frac', step: 'any', ph: '0.5' }),
        F('bool', 'combat.kite.pincer_escape', 'hunt_l_kite_pincere', { def: true }),
        F('bool', 'combat.kite.contact_disengage', 'hunt_l_kite_diseng', { def: true }),
        F('bool', 'combat.kite.arrival_finish', 'hunt_l_kite_arrival', { def: true }),
        F('bool', 'combat.kite.race_finish', 'hunt_l_kite_rfinish', { def: true }),
        F('num', 'combat.kite.failed_retreat_seconds', 'hunt_l_kite_failretreat', { unit: 'hunt_u_sec', step: 'any', ph: '3' }),
        F('bool', 'combat.kite.fight_when_cornered', 'hunt_l_kite_corner', { def: true })
      ] }
    ] },
    { id: 'survival', icon: '🛡️', l: 'hunt_sec_survival', d: 'hunt_sec_survival_d', blocks: [
      { fields: [
        F('num', 'healing.hp_percent', 'hunt_l_heal_hp', { unit: 'hunt_u_pct', min: 1, max: 100, hint: 'hunt_t_heal_hp' }),
        F('num', 'healing.interval_seconds', 'hunt_l_heal_interval', { unit: 'hunt_u_sec', step: 'any', ph: '1' }),
        F('chips', 'healing.item_codes', 'hunt_l_heal_codes', { kind: 'item' })
      ] },
      { fields: [
        F('rows', 'healing.spells', 'hunt_l_heal_spells', { keep: r => r.spell_id != null, hint: 'hunt_t_spell_auto', fields: [
          S('picker', 'spell_id', 'hunt_l_spell', { kind: 'skill' }),
          S('num', 'hp_percent', 'hunt_l_hp_pct', { unit: 'hunt_u_pct' }),
          S('num', 'interval_seconds', 'hunt_l_interval', { unit: 'hunt_u_sec', step: 'any', ph: '3' })
        ] }),
        F('rows', 'return_conditions', 'hunt_l_return', { defaults: { type: 'item_below' }, keep: r => r.type === 'item_below' ? r.item_code != null : r.value != null, fields: [
          S('select', 'type', 'hunt_l_cond_type', { options: [['item_below', 'hunt_opt_item_below'], ['hp_below', 'hunt_opt_hp_below'], ['weight_above', 'hunt_opt_weight_above']] }),
          S('picker', 'item_code', 'hunt_l_item', { kind: 'item', showIf: { k: 'type', in: ['item_below'] } }),
          S('num', 'qty', 'hunt_l_qty', { unit: 'hunt_u_cnt', int: true, showIf: { k: 'type', in: ['item_below'] } }),
          S('num', 'value', 'hunt_l_cond_value', { unit: 'hunt_u_pct', showIf: { k: 'type', in: ['hp_below', 'weight_above'] } })
        ] })
      ] },
      { fields: [
        F('num', 'protection.reserve_hits', 'hunt_l_reserve_hits', { int: true, min: 0, ph: '3', hint: 'hunt_t_reserve_hits' }),
        F('num', 'protection.avoid_radius', 'hunt_l_avoid_radius', { unit: 'hunt_u_tile', int: true, min: 0, max: 8, hint: 'hunt_t_avoid_radius' }),
        F('chips', 'protection.avoid_mobs', 'hunt_l_avoid_mobs', { kind: 'monster', hint: 'hunt_t_avoid_mobs' }),
        F('chips', 'protection.return_item_codes', 'hunt_l_return_codes', { kind: 'item', hint: 'hunt_t_return_codes' })
      ] },
      { l: 'hunt_b_damage_escape', collapsible: true, fields: [
        F('chips', 'protection.damage_escape.trigger_mobs', 'hunt_l_trigger_mobs', { kind: 'monster' }),
        F('num', 'protection.damage_escape.defend_hp_percent', 'hunt_l_defend_hp', { unit: 'hunt_u_pct', ph: '30' }),
        F('travel', 'protection.damage_escape.fallback_travel', 'hunt_l_fallback_travel', { variant: 'base' })
      ] },
      { l: 'hunt_b_stability', collapsible: true, fields: [
        F('bool', 'protection.activity_schedule.enabled', 'hunt_l_sched_enabled', { def: false }),
        F('num', 'protection.activity_schedule.run_minutes', 'hunt_l_sched_run', { unit: 'hunt_u_min', int: true, min: 1, ph: '60' }),
        F('num', 'protection.activity_schedule.rest_minutes', 'hunt_l_sched_rest', { unit: 'hunt_u_min', int: true, min: 1, ph: '10', hint: 'hunt_t_schedule' }),
        F('bool', 'protection.death_streak.enabled', 'hunt_l_ds_enabled', { def: false }),
        F('num', 'protection.death_streak.count', 'hunt_l_ds_count', { unit: 'hunt_u_cnt', int: true, min: 2, ph: '4' }),
        F('num', 'protection.death_streak.rest_minutes', 'hunt_l_ds_rest', { unit: 'hunt_u_min', int: true, min: 1, ph: '60', hint: 'hunt_t_death_streak' })
      ] },
      { fields: [
        F('rows', 'cure_items', 'hunt_l_cure_items', { hint: 'hunt_t_cure_items', fields: [
          S('chips', 'buff_ids', 'hunt_l_cure_trigger', { kind: 'buff', hint: 'hunt_t_cure_trigger' }),
          S('chips', 'item_codes', 'hunt_l_item_codes', { kind: 'item' }),
          S('chips', 'spell_ids', 'hunt_l_cure_spells', { kind: 'skill', hint: 'hunt_t_spell_auto' }),
          S('bool', 'prefer_spell', 'hunt_l_cure_prefer', { def: false }),
          S('num', 'interval_seconds', 'hunt_l_interval', { unit: 'hunt_u_sec', step: 'any', ph: '2' })
        ] })
      ] }
    ] },
    { id: 'party', icon: '👥', l: 'hunt_sec_party', d: 'hunt_sec_party_d', blocks: [
      { fields: [
        F('text', 'party.leader', 'hunt_l_party_leader', { hint: 'hunt_t_party' }),
        F('tags', 'party.members', 'hunt_l_party_members', { hint: 'hunt_t_party_members' }),
        F('text', 'party.loot_master', 'hunt_l_party_loot', { hint: 'hunt_t_party_loot' }),
        F('bool', 'party.follow', 'hunt_l_party_follow', { def: true }),
        F('bool', 'party.focus', 'hunt_l_party_focus', { def: true })
      ] }
    ] },
    { id: 'buffs', icon: '✨', l: 'hunt_sec_buffs', d: 'hunt_sec_buffs_d', blocks: [
      { fields: [
        F('chips', 'maintain_buffs', 'hunt_l_maintain_buffs', { kind: 'skill', hint: 'hunt_t_spell_auto' }),
        F('buffsByMap', 'maintain_buffs_by_map', 'hunt_l_buffs_by_map', {}),
        F('num', 'buff_recast_seconds', 'hunt_l_buff_recast', { unit: 'hunt_u_sec', step: 'any', ph: '60' })
      ] },
      { fields: [
        F('rows', 'maintain_items', 'hunt_l_maintain_items', { fields: [
          S('chips', 'item_codes', 'hunt_l_item_codes', { kind: 'item' }),
          S('chips', 'buff_ids', 'hunt_l_mi_buffs', { kind: 'buff', hint: 'hunt_t_mi_buffs' }),
          S('num', 'interval_seconds', 'hunt_l_interval', { unit: 'hunt_u_sec', step: 'any', ph: '300' }),
          S('num', 'mp_below_percent', 'hunt_l_mp_below', { unit: 'hunt_u_pct' })
        ] })
      ] },
      { l: 'hunt_b_event_buff', collapsible: true, fields: [
        F('bool', 'event_buff.enabled', 'hunt_l_eb_enabled', { def: false }),
        F('chips', 'event_buff.buff_ids', 'hunt_l_eb_buffids', { kind: 'buff' }),
        F('picker', 'event_buff.object_name', 'hunt_l_eb_object', { kind: 'npc' }),
        F('picker', 'event_buff.scroll_item_code', 'hunt_l_eb_scroll_code', { kind: 'item' }),
        F('num', 'event_buff.scroll_slot', 'hunt_l_eb_scroll_slot', { unit: 'hunt_u_slot', int: true, min: 0, hint: 'hunt_t_eb_scroll_slot' }),
        F('text', 'event_buff.scroll_title', 'hunt_l_eb_scroll_title'),
        F('text', 'event_buff.section', 'hunt_l_eb_section'),
        F('text', 'event_buff.destination', 'hunt_l_eb_dest'),
        F('picker', 'event_buff.town_map', 'hunt_l_eb_town_map', { kind: 'map' }),
        F('picker', 'event_buff.town_region', 'hunt_l_eb_town_region', { kind: 'regionName' }),
        F('text', 'event_buff.menu_layout', 'hunt_l_eb_layout', { ph: 'TalkingScrollLayout' }),
        F('group', 'event_buff.warehouse', 'hunt_l_eb_wh', { fields: xy() }),
        F('group', 'event_buff.object_point', 'hunt_l_eb_obj_pt', { fields: xy() }),
        F('group', 'event_buff.resume_point', 'hunt_l_eb_resume', { fields: xyMap() }),
        F('travel', 'event_buff.hunt_travel', 'hunt_l_eb_hunt_travel', { variant: 'hunt' })
      ] },
      { l: 'hunt_b_haste', collapsible: true, fields: [
        F('bool', 'haste_shop.enabled', 'hunt_l_haste_en', { def: true }),
        F('chips', 'haste_shop.names', 'hunt_l_haste_names', { kind: 'npc' }),
        F('num', 'haste_shop.price', 'hunt_l_haste_price', { unit: 'hunt_u_adena', int: true, min: 200, ph: '200' }),
        F('num', 'haste_shop.radius', 'hunt_l_haste_radius', { unit: 'hunt_u_tile', int: true, ph: '15' }),
        F('picker', 'haste_shop.buff_id', 'hunt_l_haste_buff', { kind: 'skill', ph: '42' }),
        F('num', 'haste_shop.retry_seconds', 'hunt_l_haste_retry', { unit: 'hunt_u_sec', step: 'any', ph: '300' }),
        F('num', 'haste_shop.full_seconds', 'hunt_l_haste_full', { unit: 'hunt_u_sec', step: 'any', ph: '7000' }),
        F('num', 'haste_shop.timeout_seconds', 'hunt_l_haste_timeout', { unit: 'hunt_u_sec', step: 'any', ph: '60' }),
        F('bool', 'haste_shop.force', 'hunt_l_haste_force', { def: false, hint: 'hunt_t_haste_force' }),
        F('bool', 'haste_shop.hold', 'hunt_l_haste_hold', { def: false }),
        F('travel', 'haste_shop.travel', 'hunt_l_haste_travel', { variant: 'hunt' })
      ] },
      { l: 'hunt_b_transform', collapsible: true, fields: [
        F('bool', 'transform.enabled', 'hunt_l_tf_en', { def: true }),
        F('chips', 'transform.item_codes', 'hunt_l_tf_items', { kind: 'item', hint: 'hunt_t_tf_items' }),
        F('text', 'transform.entry', 'hunt_l_tf_entry', { hint: 'hunt_t_tf_entry' }),
        F('chips', 'transform.buff_ids', 'hunt_l_tf_buffs', { kind: 'buff' }),
        F('num', 'transform.duration_seconds', 'hunt_l_tf_duration', { unit: 'hunt_u_sec', step: 'any', ph: '1800' })
      ] },
      { l: 'hunt_b_npc_contract', collapsible: true, fields: [
        F('bool', 'npc_contract.enabled', 'hunt_l_nc_en', { def: true, hint: 'hunt_t_nc_en' }),
        F('picker', 'npc_contract.npc', 'hunt_l_nc_npc', { kind: 'npc' }),
        F('group', 'npc_contract.npc_point', 'hunt_l_nc_point', { fields: xy() }),
        F('travel', 'npc_contract.scroll', 'hunt_l_nc_scroll', { variant: 'base' }),
        F('text', 'npc_contract.contract', 'hunt_l_nc_contract'),
        F('text', 'npc_contract.settle', 'hunt_l_nc_settle'),
        F('text', 'npc_contract.renew', 'hunt_l_nc_renew'),
        F('chips', 'npc_contract.item_codes', 'hunt_l_nc_items', { kind: 'item' }),
        F('map', 'npc_contract.values', 'hunt_l_nc_values', { kind: 'item', vUnit: 'hunt_u_adena', vInt: true }),
        F('num', 'npc_contract.settle_value', 'hunt_l_nc_settle_value', { unit: 'hunt_u_adena', int: true, ph: '3000' }),
        F('chips', 'npc_contract.kill_names', 'hunt_l_nc_kill_names', { kind: 'monster', hint: 'hunt_t_nc_kill_names' }),
        F('num', 'npc_contract.kills_without_item', 'hunt_l_nc_kills', { int: true, ph: '8' })
      ] }
    ] },
    { id: 'restock', icon: '🏪', l: 'hunt_sec_restock', d: 'hunt_sec_restock_d', blocks: [
      { fields: [ F('travel', 'return_scroll', 'hunt_l_return_scroll', { variant: 'base' }) ] },
      { l: 'hunt_b_restock', fields: [
        F('rows', 'restock.buy', 'hunt_l_restock_buy', { keep: r => r.item_code != null, fields: buyFields(true, true) }),
        F('rows', 'restock.sell', 'hunt_l_restock_sell', { keep: r => r.item_code != null, fields: sellFields() }),
        F('picker', 'restock.merchant', 'hunt_l_restock_merchant', { kind: 'npc' }),
        F('chips', 'restock.sell_merchants', 'hunt_l_sell_merchants', { kind: 'npc' }),
        F('num', 'restock.weight_limit_percent', 'hunt_l_weight_limit', { unit: 'hunt_u_pct', hint: 'hunt_t_weight_limit' }),
        F('travel', 'restock.travel', 'hunt_l_restock_travel', { variant: 'shop' }),
        F('travel', 'restock.hunt_travel', 'hunt_l_hunt_travel', { variant: 'hunt' })
      ] },
      { l: 'hunt_b_after_return', fields: [
        F('num', 'after_return.town_wait_sec', 'hunt_l_town_wait', { unit: 'hunt_u_sec', step: 'any', ph: '30' }),
        F('bool', 'after_return.resume_hunt', 'hunt_l_resume_hunt', { def: true }),
        F('num', 'after_return.recover_hp_percent', 'hunt_l_recover_hp', { unit: 'hunt_u_pct', max: 95, ph: '95' }),
        F('picker', 'after_return.map_id', 'hunt_l_ar_map', { kind: 'map' }),
        F('picker', 'after_return.region', 'hunt_l_ar_region', { kind: 'regionName' }),
        F('group', 'after_return.point', 'hunt_l_ar_point', { fields: xy() }),
        F('bool', 'hold_in_town', 'hunt_l_hold_town', { def: false, hint: 'hunt_t_hold_town' })
      ] },
      { l: 'hunt_b_warehouse', collapsible: true, fields: [
        F('bool', 'warehouse.enabled', 'hunt_l_wh_en', { def: false }),
        F('chips', 'warehouse.item_codes', 'hunt_l_wh_items', { kind: 'item' }),
        F('chips', 'warehouse.preserve_codes', 'hunt_l_wh_preserve', { kind: 'item' }),
        F('chips', 'warehouse.always_codes', 'hunt_l_wh_always', { kind: 'item' }),
        F('rows', 'warehouse.keep', 'hunt_l_wh_keep', { hint: 'hunt_t_wh_keep', keep: r => Array.isArray(r.codes) && r.codes.length > 0 && r.quantity != null, fields: [
          S('chips', 'codes', 'hunt_l_item_codes', { kind: 'item' }),
          S('num', 'quantity', 'hunt_l_qty', { int: true, min: 0 })
        ] }),
        F('num', 'warehouse.weight_percent', 'hunt_l_wh_weight', { unit: 'hunt_u_pct' }),
        F('num', 'warehouse.target_weight_percent', 'hunt_l_wh_target', { unit: 'hunt_u_pct' }),
        F('text', 'warehouse.menu_section', 'hunt_l_wh_section'),
        F('text', 'warehouse.menu_deposit', 'hunt_l_wh_deposit'),
        F('picker', 'warehouse.npc_name', 'hunt_l_wh_npc', { kind: 'npc' }),
        F('chips', 'warehouse.npc_names', 'hunt_l_wh_npcs', { kind: 'npc' }),
        F('text', 'warehouse.menu_withdraw', 'hunt_l_wh_withdraw_menu', { ph: '물건을 찾는다' }),
        F('chips', 'warehouse.withdraw.codes', 'hunt_l_wh_withdraw_codes', { kind: 'item' }),
        F('chips', 'warehouse.withdraw.names', 'hunt_l_wh_withdraw_names', { kind: 'itemName', hint: 'hunt_t_wh_withdraw_names' }),
        F('text', 'warehouse.withdraw.token', 'hunt_l_wh_withdraw_token', { hint: 'hunt_t_wh_withdraw_token' }),
        F('travel', 'warehouse.travel', 'hunt_l_wh_travel', { variant: 'base' })
      ] },
      { l: 'hunt_b_food', collapsible: true, fields: [
        F('bool', 'food.enabled', 'hunt_l_food_en', { def: true }),
        F('num', 'food.target_percent', 'hunt_l_food_target', { unit: 'hunt_u_pct', ph: '51' }),
        F('num', 'food.min_percent', 'hunt_l_food_min', { unit: 'hunt_u_pct', ph: '50' }),
        F('num', 'food.interval_seconds', 'hunt_l_food_interval', { unit: 'hunt_u_sec', step: 'any', ph: '1.5' }),
        F('chips', 'food.item_codes', 'hunt_l_food_codes', { kind: 'item', ph: '10, 9, 37, 60' })
      ] },
      { l: 'hunt_b_spell_shop', collapsible: true, fields: [
        F('rows', 'spell_shop.buy', 'hunt_l_ss_buy', { keep: r => r.item_code != null, fields: buyFields(true) }),
        F('rows', 'spell_shop.sell', 'hunt_l_ss_sell', { keep: r => r.item_code != null, fields: sellFields() }),
        F('picker', 'spell_shop.merchant', 'hunt_l_ss_merchant', { kind: 'npc' }),
        F('chips', 'spell_shop.sell_merchants', 'hunt_l_sell_merchants', { kind: 'npc' }),
        F('num', 'spell_shop.weight_limit_percent', 'hunt_l_weight_limit', { unit: 'hunt_u_pct' }),
        F('travel', 'spell_shop.travel', 'hunt_l_ss_travel', { variant: 'shop' }),
        F('travel', 'spell_shop.hunt_travel', 'hunt_l_ss_hunt_travel', { variant: 'hunt' })
      ] },
      { fields: [
        F('rows', 'learn_books', 'hunt_l_learn_books', { hint: 'hunt_t_learn_books', keep: r => r.item_code != null, fields: [
          S('picker', 'item_code', 'hunt_l_item', { kind: 'item' }),
          S('picker', 'spell_id', 'hunt_l_spell', { kind: 'skill' })
        ] })
      ] }
    ] },
    { id: 'explore', icon: '🔍', l: 'hunt_sec_explore', d: 'hunt_sec_explore_d', blocks: [
      { l: 'hunt_b_exploration', fields: [
        F('bool', 'exploration.enabled', 'hunt_l_exp_en', { def: false }),
        F('chips', 'exploration.exclude_region_ids', 'hunt_l_exp_exclude', { kind: 'region' }),
        F('num', 'exploration.margin_tiles', 'hunt_l_exp_margin', { unit: 'hunt_u_tile', int: true, min: 0, max: 64, ph: '16' }),
        F('num', 'exploration.revisit_seconds', 'hunt_l_exp_revisit', { unit: 'hunt_u_sec', step: 'any', ph: '60' }),
        F('num', 'exploration.memory_seconds', 'hunt_l_exp_memory', { unit: 'hunt_u_sec', step: 'any', ph: '1800' }),
        F('num', 'exploration.enter_after_idle_seconds', 'hunt_l_exp_idle', { unit: 'hunt_u_sec', step: 'any' }),
        F('num', 'exploration.progress_seconds', 'hunt_l_exp_progress', { unit: 'hunt_u_sec', step: 'any', ph: '20' }),
        F('num', 'exploration.unreachable_seconds', 'hunt_l_exp_unreach', { unit: 'hunt_u_sec', step: 'any', ph: '600' })
      ] },
      { l: 'hunt_b_loot', fields: [
        F('bool', 'loot.pick_others', 'hunt_l_loot_others', { def: true, hint: 'hunt_t_loot_others' }),
        F('bool', 'loot.scavenge_equipment', 'hunt_l_loot_scav', { def: false }),
        F('num', 'loot.weight_limit_percent', 'hunt_l_loot_weight', { unit: 'hunt_u_pct', ph: '100' }),
        F('num', 'loot.own_priority', 'hunt_l_loot_own', { int: true, min: 0, max: 100, ph: '100' }),
        F('chips', 'loot.exclude_names', 'hunt_l_loot_exclude', { kind: 'itemName' }),
        F('num', 'loot.memory_seconds', 'hunt_l_loot_memory', { unit: 'hunt_u_sec', step: 'any', ph: '120' }),
        F('num', 'loot.view_tiles', 'hunt_l_loot_view', { unit: 'hunt_u_tile', int: true, ph: '12' }),
        F('num', 'loot.return_tiles', 'hunt_l_loot_return', { unit: 'hunt_u_tile', int: true, ph: '24' }),
        F('num', 'loot.near_tiles', 'hunt_l_loot_near', { unit: 'hunt_u_tile', int: true, ph: '3' }),
        F('num', 'loot.clear_radius', 'hunt_l_loot_clear', { unit: 'hunt_u_tile', int: true, ph: '8' }),
        F('num', 'loot.kill_seconds', 'hunt_l_loot_kill', { unit: 'hunt_u_sec', step: 'any', ph: '3', hint: 'hunt_t_loot_kill' }),
        F('num', 'loot.own_protect_seconds', 'hunt_l_loot_protect', { unit: 'hunt_u_sec', step: 'any', ph: '10.5' })
      ] }
    ] },
    { id: 'advanced', icon: '⚙️', l: 'hunt_sec_advanced', d: 'hunt_sec_advanced_d', showKey: true, blocks: [
      { fields: [
        F('num', 'walk_stall_seconds', 'hunt_l_walk_stall', { unit: 'hunt_u_sec', step: 'any', ph: '2' }),
        F('num', 'patrol_progress_seconds', 'hunt_l_patrol_progress', { unit: 'hunt_u_sec', step: 'any', ph: '20' }),
        F('num', 'seek_progress_seconds', 'hunt_l_seek_progress', { unit: 'hunt_u_sec', step: 'any', ph: '8', hint: 'hunt_t_seek_progress' }),
        F('num', 'arrow_retry_seconds', 'hunt_l_arrow_retry', { unit: 'hunt_u_sec', step: 'any', ph: '1' }),
        F('bool', 'qa_session_stop', 'hunt_l_qa_stop', { def: false }),
        F('bool', 'qa_play_paused', 'hunt_l_qa_pause', { def: false }),
        F('num', 'world_fps', 'hunt_l_world_fps', { int: true, min: 0, max: 30, ph: '24', hint: 'hunt_t_world_fps' })
      ] },
      { fields: [ F('travel', 'hunt_travel', 'hunt_l_hunt_travel_top', { variant: 'hunt' }) ] },
      { l: 'hunt_b_npc_travel', collapsible: true, fields: [
        F('travel', 'npc_travel.scroll', 'hunt_l_nt_scroll', { variant: 'base' }),
        F('text', 'npc_travel.option', 'hunt_l_nt_option'),
        F('tags', 'npc_travel.steps', 'hunt_l_nt_steps'),
        F('chips', 'npc_travel.npc_names', 'hunt_l_nt_npcs', { kind: 'npc' }),
        F('picker', 'npc_travel.target.map_id', 'hunt_l_nt_map', { kind: 'map' }),
        F('num', 'npc_travel.target.l', 'hunt_l_box_l', { int: true }),
        F('num', 'npc_travel.target.t', 'hunt_l_box_t', { int: true }),
        F('num', 'npc_travel.target.r', 'hunt_l_box_r', { int: true }),
        F('num', 'npc_travel.target.b', 'hunt_l_box_b', { int: true })
      ] },
      { l: 'hunt_b_route', collapsible: true, fields: [
        F('text', 'route_capture.url', 'hunt_l_rc_url'),
        F('text', 'route_capture.token', 'hunt_l_rc_token'),
        F('text', 'route_capture.source_revision', 'hunt_l_rc_rev')
      ] },
      { fields: [
        F('rows', 'patrol_points', 'hunt_l_patrol_points_top', { hint: 'hunt_t_patrol_points_top', keep: r => r.x != null && r.y != null, fields: xy() }),
        F('map', 'spell_mp', 'hunt_l_spell_mp', { kind: 'skill', vUnit: 'hunt_u_mp', vInt: true })
      ] }
    ] },
    { id: 'json', icon: '🧩', l: 'hunt_sec_json', d: 'hunt_sec_json_d', blocks: [ { custom: 'json' } ] }
  ];
  // index of top-level fields by fid
  const ALL_FIELDS = [];
  (function collectFields() {
    for (const sec of SECTIONS) for (const blk of sec.blocks) if (blk.fields) for (const f of blk.fields) { f._fid = ALL_FIELDS.length; ALL_FIELDS.push(f); }
  })();

  // ────────────────────────────────────────────── rendering ──────────────────────────────────────────────
  function unitSpan(field) { return field.unit ? `<span class="hf-unit">${esc(T(field.unit))}</span>` : ''; }
  function keyNote(field, show) { return (show && field.path) ? `<code class="hf-key" title="${esc(T('hunt_key'))}">${esc(field.path.split('.').pop())}</code>` : ''; }

  // Render a leaf/complex field. idAttr is the attribute string placed on .hf-field.
  function renderField(I, field, val, idAttr, showKey) {
    const L = esc(T(field.l));
    const hint = field.hint ? `<div class="hint">${esc(T(field.hint))}</div>` : '';
    const key = keyNote(field, showKey);
    const err = '<div class="error"></div>';
    const search = (T(field.l) + ' ' + (field.hint ? T(field.hint) : '') + ' ' + (field.path || '')).toLowerCase();
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search)}"` : '';
    const open = (cls, extra) => `<div class="hf-field ${cls}" ${idAttr}${sAttr}${extra || ''}>`;

    if (field.type === 'bool') {
      // A value equal to its default is omitted on save (readLeaf), so an absent value renders as the default.
      const on = val == null ? field.def === true : val === true;
      return open('hf-bool') + `<label class="switch"><input type="checkbox"${on ? ' checked' : ''}><span>${L} ${key}</span></label>${hint}</div>`;
    }
    if (field.type === 'num') {
      return open('hf-leaf') + `<label class="label">${L} ${key}</label>`
        + `<div class="hf-num"><input type="number"${field.step ? ` step="${field.step}"` : ''}${field.min != null ? ` min="${field.min}"` : ''}${field.max != null ? ` max="${field.max}"` : ''} value="${val == null ? '' : esc(val)}" placeholder="${esc(field.ph || '')}">${unitSpan(field)}</div>${hint}${err}</div>`;
    }
    if (field.type === 'text') {
      return open('hf-leaf') + `<label class="label">${L} ${key}</label>`
        + `<input type="text" value="${val == null ? '' : esc(val)}" placeholder="${esc(field.ph || '')}">${hint}${err}</div>`;
    }
    if (field.type === 'select') {
      const opts = (field.options || []).map(([v, l]) => `<option value="${esc(v)}"${String(val) === String(v) ? ' selected' : ''}>${esc(T(l))}</option>`).join('');
      return open('hf-leaf') + `<label class="label">${L} ${key}</label><select>${opts}</select>${hint}${err}</div>`;
    }
    if (field.type === 'picker') {
      return open('hf-leaf') + `<label class="label">${L} ${key}</label>${P.html(field.kind, val)}${hint}${err}</div>`;
    }
    if (field.type === 'chips') {
      const arr = Array.isArray(val) ? val : [];
      return open('hf-chipsf') + `<label class="label">${L} ${key}</label>`
        + `<div class="hf-chips">${arr.map(v => chipHtml(field.kind, v)).join('')}</div>`
        + `<div class="hf-chipctl">${P.html(field.kind, null, { chips: true })}</div>${hint}${err}</div>`;
    }
    if (field.type === 'tags') {
      const arr = Array.isArray(val) ? val : [];
      return open('hf-chipsf') + `<label class="label">${L} ${key}</label>`
        + `<div class="hf-chips">${arr.map(v => chipHtml('tag', v)).join('')}</div>`
        + `<div class="hf-chipctl"><input type="text" class="hf-taginput" placeholder="${esc(field.ph || T('hunt_tag_ph'))}"></div>${hint}${err}</div>`;
    }
    if (field.type === 'group') {
      return open('hf-group') + `<div class="hf-group-l">${L} ${key}</div><div class="hf-sub hf-grid-sub">${renderSub(I, field.fields, isObj(val) ? val : {}, false)}</div></div>`;
    }
    if (field.type === 'travel') {
      return renderTravel(I, field, val, idAttr, search);
    }
    if (field.type === 'rows') {
      return renderRows(I, field, Array.isArray(val) ? val : [], idAttr, search);
    }
    if (field.type === 'map') {
      return renderMap(I, field, isObj(val) ? val : {}, idAttr, search);
    }
    if (field.type === 'buffsByMap') {
      return renderBuffsByMap(I, field, isObj(val) ? val : {}, idAttr, search);
    }
    if (field.type === 'huntMaps') {
      return renderHuntMaps(I, field, Array.isArray(val) ? val : [], idAttr, search);
    }
    return '';
  }

  // sub-fields inside a group/travel/row; baseObj supplies current values
  function renderSub(I, fields, obj, showKey) {
    let html = '';
    for (const f of fields) {
      if (f.adv) { // collapsed advanced group of subfields
        html += `<details class="hf-adv"><summary>${esc(T('hunt_badv_buy'))}</summary><div class="hf-grid-sub">${renderSub(I, f.fields, obj, showKey)}</div></details>`;
        continue;
      }
      const attr = `data-k="${esc(f.k)}" data-sk="${esc(f.k)}"` + (f.showIf ? ` data-showif="${esc(f.showIf.k + ':' + f.showIf.in.join(','))}"` : '');
      html += renderField(I, f, obj[f.k], attr, showKey);
    }
    return html;
  }

  function renderTravel(I, field, val, idAttr, search) {
    const obj = isObj(val) ? val : {};
    const fields = travelFields(field.variant);
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search)}"` : '';
    return `<div class="hf-field hf-travel" ${idAttr}${sAttr} data-variant="${field.variant}">`
      + `<button type="button" class="hf-travel-head"><span class="hf-tw-caret">▸</span><span class="hf-tw-l">${esc(T(field.l))}</span><span class="hf-tw-sum">${esc(travelSummary(obj))}</span></button>`
      + `<div class="hf-sub hf-travel-body hf-grid-sub" hidden>${renderSub(I, fields, obj, false)}</div></div>`;
  }
  function travelSummary(obj) {
    if (!obj || !Object.keys(obj).length) return T('hunt_travel_unset');
    const parts = [];
    if (obj.scroll_item_code != null) parts.push(App.cat.item(obj.scroll_item_code));
    else if (obj.scroll_title) parts.push(obj.scroll_title);
    if (obj.destination) parts.push(obj.destination);
    else if (obj.town_map != null) parts.push(App.cat.map(obj.town_map));
    if (!parts.length) return T('hunt_travel_summary', '✓');
    return parts.join(' · ');
  }

  function ctlId(I, kind, field) { const id = ++I.uidc; I.ctl.set(String(id), { kind, field }); return id; }
  // Rows are listed one summary line each (every set value, labelled); a row is added or edited in a modal, so long
  // lists (sale rows with their trips, maintained items) keep the page short.
  function renderRows(I, field, arr, idAttr, search) {
    const id = ctlId(I, 'rows', field);
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search)}"` : '';
    const hint = field.hint ? `<div class="hint">${esc(T(field.hint))}</div>` : '';
    return `<div class="hf-field hf-rowsf" ${idAttr}${sAttr} data-ctl="${id}">`
      + `<div class="hf-rows-head"><span class="label">${esc(T(field.l))}</span><button type="button" class="btn sm secondary hf-rowadd">${esc(T('hunt_add_row'))}</button></div>${hint}`
      + `<div class="hf-rows hf-rowlist">${arr.map(r => rowHtml(I, field, r)).join('')}</div>`
      + `<div class="hint hf-rows-empty"${arr.length ? ' hidden' : ''}>${esc(T('hunt_rows_empty'))}</div><div class="error"></div></div>`;
  }
  function rowHtml(I, field, row) {
    row = row || {};
    return `<div class="hf-row hf-rowline" data-value="${esc(JSON.stringify(row))}">`
      + `<button type="button" class="hf-rowedit" title="${esc(T('edit'))}">${rowSummary(field.fields, row)}</button>`
      + `<button type="button" class="btn ghost icon sm hf-rowdel" aria-label="${esc(T('hunt_remove'))}">✕</button></div>`;
  }
  // One cell per set value: the first field unlabelled (the row's subject), the rest as "label value".
  function rowSummary(fields, row) {
    const flat = [];
    (function walk(list) { for (const f of list) { if (f.adv) walk(f.fields); else flat.push(f); } })(fields);
    const cells = [];
    flat.forEach((f, i) => {
      const v = row[f.k];
      let text = null;
      if (f.type === 'picker' && v != null && v !== '') text = P.label(f.kind, v);
      else if ((f.type === 'chips' || f.type === 'tags') && Array.isArray(v) && v.length) text = v.map(x => f.type === 'chips' ? P.label(f.kind, x) : String(x)).join(', ');
      else if (f.type === 'num' && v != null) text = String(v) + (f.unit ? ' ' + T(f.unit) : '');
      else if (f.type === 'text' && v) text = String(v);
      else if (f.type === 'select' && v != null) { const o = (f.options || []).find(([ov]) => String(ov) === String(v)); text = o ? T(o[1]) : String(v); }
      else if (f.type === 'bool' && v != null && v !== f.def) text = v ? '✓' : '✗';
      else if (f.type === 'group' && isObj(v) && (v.x != null || v.y != null)) text = `(${v.x ?? '?'}, ${v.y ?? '?'})`;
      else if (f.type === 'travel' && isObj(v) && Object.keys(v).length) text = travelSummary(v);
      if (text == null) return;
      cells.push(i === 0 ? `<span class="hf-cell hf-cell-main">${esc(text)}</span>`
        : `<span class="hf-cell"><span class="hf-cell-l">${esc(T(f.l))}</span>${esc(text)}</span>`);
    });
    return cells.length ? cells.join('') : `<span class="hf-cell muted">${esc(T('hunt_row_blank'))}</span>`;
  }
  // The row editor sits inside the form root, so the form's delegated handlers (pickers, chips, travel, showIf) serve it.
  function openRowModal(I, cf, rowEl) {
    const { field } = I.ctl.get(cf.dataset.ctl);
    let value = field.defaults ? clone(field.defaults) : {};
    if (rowEl) { try { value = JSON.parse(rowEl.dataset.value || '{}'); } catch (_) { value = {}; } }
    closeRowModal(I);
    const wrap = document.createElement('div');
    wrap.className = 'hf-modal-back';
    wrap.innerHTML = `<div class="hf-modal" role="dialog" aria-modal="true" aria-label="${esc(T(field.l))}">`
      + `<div class="hf-modal-head"><span>${esc(T(field.l))}</span><button type="button" class="btn ghost icon sm hf-modal-cancel" aria-label="${esc(T('close'))}">✕</button></div>`
      + `<div class="hf-sub hf-grid-sub hf-modal-body">${renderSub(I, field.fields, value, false)}</div>`
      + `<div class="error hf-modal-err"></div><div class="hf-modal-actions"><button type="button" class="btn secondary hf-modal-cancel">${esc(T(I.readOnly ? 'close' : 'cancel'))}</button>`
      + (I.readOnly ? '' : `<button type="button" class="btn primary hf-modal-save">${esc(T('save'))}</button>`) + `</div></div>`;
    I.root.querySelector('.hf').appendChild(wrap);
    I.modal = { wrap, cf, rowEl, field, base: value };
    applyShowIf(I);
    if (I.readOnly) wrap.querySelectorAll('.hf-modal-body input,.hf-modal-body select,.hf-modal-body textarea,.hf-modal-body button')
      .forEach(el => { if (!el.closest('.hf-travel-head')) el.disabled = true; });
  }
  function closeRowModal(I) {
    if (I.modal) { I.modal.wrap.remove(); I.modal = null; }
  }
  function saveRowModal(I) {
    const m = I.modal;
    const errors = [];
    const obj = readContainer(m.wrap.querySelector('.hf-modal-body'), m.field.fields, m.base, m.field.path || '', errors) || {};
    const err = m.wrap.querySelector('.hf-modal-err');
    if (errors.length) { err.textContent = errors.map(e => e.label + ': ' + e.message).join(' / '); return; }
    if (m.field.keep && !m.field.keep(obj)) { err.textContent = T('hunt_row_incomplete'); return; }
    const html = rowHtml(I, m.field, obj);
    if (m.rowEl && m.rowEl.isConnected) m.rowEl.outerHTML = html;
    else m.cf.querySelector(':scope > .hf-rows').insertAdjacentHTML('beforeend', html);
    m.cf.querySelector(':scope > .hf-rows-empty').hidden = true;
    markTouched(I, m.cf);
    closeRowModal(I);
  }

  function renderMap(I, field, dict, idAttr, search) {
    const id = ctlId(I, 'map', field);
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search)}"` : '';
    const hint = field.hint ? `<div class="hint">${esc(T(field.hint))}</div>` : '';
    const rows = Object.entries(dict).map(([k, v]) => mapRowHtml(field, k, v)).join('');
    return `<div class="hf-field hf-mapf" ${idAttr}${sAttr} data-ctl="${id}">`
      + `<div class="hf-rows-head"><span class="label">${esc(T(field.l))}</span><button type="button" class="btn sm secondary hf-mapadd">${esc(T('hunt_add_row'))}</button></div>${hint}`
      + `<div class="hf-rows">${rows}</div><div class="error"></div></div>`;
  }
  function mapRowHtml(field, key, val) {
    return `<div class="hf-row"><div class="hf-sub hf-grid-sub">`
      + `<div class="hf-field hf-subf"><label class="label">${esc(T('hunt_l_item'))}</label>${P.html(field.kind, key)}</div>`
      + `<div class="hf-field hf-subf hf-leaf"><label class="label">${esc(T(field.vUnit || 'hunt_u_weight'))}</label><div class="hf-num"><input type="number" class="hf-mapval"${field.vInt ? '' : ' step="any"'} value="${val == null ? '' : esc(val)}" placeholder="${esc(field.vPh || '')}"></div></div>`
      + `</div><button type="button" class="btn ghost icon sm hf-rowdel" aria-label="${esc(T('hunt_remove'))}">✕</button></div>`;
  }

  function renderBuffsByMap(I, field, dict, idAttr, search) {
    const id = ctlId(I, 'buffsByMap', field);
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search)}"` : '';
    const rows = Object.entries(dict).map(([k, v]) => buffMapRowHtml(field, k, v)).join('');
    return `<div class="hf-field hf-mapf" ${idAttr}${sAttr} data-ctl="${id}">`
      + `<div class="hf-rows-head"><span class="label">${esc(T(field.l))}</span><button type="button" class="btn sm secondary hf-mapadd">${esc(T('hunt_add_row'))}</button></div>`
      + `<div class="hf-rows">${rows}</div><div class="error"></div></div>`;
  }
  function buffMapRowHtml(field, mapId, ids) {
    const arr = Array.isArray(ids) ? ids : [];
    return `<div class="hf-row"><div class="hf-sub hf-grid-sub">`
      + `<div class="hf-field hf-subf"><label class="label">${esc(T('hunt_l_map'))}</label>${P.html('map', mapId)}</div>`
      + `<div class="hf-field hf-subf hf-chipsf"><label class="label">${esc(T('hunt_l_maintain_buffs'))}</label><div class="hf-chips">${arr.map(v => chipHtml('skill', v)).join('')}</div><div class="hf-chipctl">${P.html('skill', null, { chips: true })}</div></div>`
      + `</div><button type="button" class="btn ghost icon sm hf-rowdel" aria-label="${esc(T('hunt_remove'))}">✕</button></div>`;
  }

  // ── hunt maps (level bands + patrol canvas) ──
  function renderHuntMaps(I, field, arr, idAttr, search) {
    const sAttr = idAttr.indexOf('data-fid') >= 0 ? ` data-s="${esc(search + ' patrol 순회 좌표 사냥맵 map')}"` : '';
    const hint = field.hint ? `<div class="hint">${esc(T(field.hint))}</div>` : '';
    return `<div class="hf-field hf-huntmaps" ${idAttr}${sAttr}>`
      + `<div class="hf-rows-head"><span class="label">${esc(T(field.l))}</span><button type="button" class="btn sm secondary hm-add">${esc(T('hunt_add_map'))}</button></div>${hint}`
      + `<div class="hf-maps">${arr.map((r, i) => huntMapRow(r, i)).join('')}</div></div>`;
  }
  function huntMapRow(r, i) {
    r = r || {};
    const pts = r.patrol_points || [];
    return `<div class="hf-maprow hm-row" data-ri="${i}" data-original="${esc(JSON.stringify(r))}">`
      + `<div class="hm-main">`
      + `<div class="hf-field hf-subf hf-leaf hm-mini"><label class="label">${esc(T('hunt_l_lv_min'))}</label><input type="number" class="hm-min" value="${r.min_level == null ? '' : esc(r.min_level)}"></div>`
      + `<div class="hf-field hf-subf hf-leaf hm-mini"><label class="label">${esc(T('hunt_l_lv_max'))}</label><input type="number" class="hm-max" value="${r.max_level == null ? '' : esc(r.max_level)}"></div>`
      + `<div class="hf-field hf-subf"><label class="label">${esc(T('hunt_l_map'))}</label>${P.html('map', r.map_id)}</div>`
      + `<div class="hf-field hf-subf"><label class="label">${esc(T('hunt_l_region'))}</label>${P.html('region', r.region_id)}</div>`
      + `<button type="button" class="btn sm secondary hm-toggle">${esc(T('hunt_points_n', pts.length))}</button>`
      + `<input type="hidden" class="hm-patrol" value="${esc(JSON.stringify(pts))}">`
      + `<button type="button" class="btn ghost icon sm hm-del" aria-label="${esc(T('hunt_remove'))}">✕</button>`
      + `</div>`
      + `<details class="hf-adv hm-entry"><summary>${esc(T('hunt_l_entry'))}</summary><div class="hf-grid-sub">`
      + `<div class="hf-field hf-subf"><label class="label">${esc(T('hunt_l_entry_map'))}</label>${P.html('map', (r.entry || {}).map_id)}</div>` + miniNum('hm-ex', 'hunt_l_entry_x', (r.entry || {}).x) + miniNum('hm-ey', 'hunt_l_entry_y', (r.entry || {}).y)
      + miniNum('hm-px', 'hunt_l_push_x', ((r.entry || {}).push || {}).x) + miniNum('hm-py', 'hunt_l_push_y', ((r.entry || {}).push || {}).y)
      + `</div><div class="hf-avoid"><label class="label">${esc(T('hunt_l_avoid_tiles'))}</label><input type="hidden" class="hm-avoid" value="${esc(JSON.stringify(r.avoid_tiles || []))}"><div class="hf-avoid-list">${avoidChips(r.avoid_tiles || [])}</div><div class="row tight"><input type="number" class="hm-avx" placeholder="X" style="width:72px"><input type="number" class="hm-avy" placeholder="Y" style="width:72px"><button type="button" class="btn sm secondary hm-avadd">${esc(T('hunt_add_row'))}</button></div></div>`
      + `<div class="hf-avoid"><label class="label">${esc(T('hunt_l_approach'))}</label><input type="hidden" class="hm-approach" value="${esc(JSON.stringify(r.approach_points || []))}"><div class="hf-approach-list">${approachChips(r.approach_points || [])}</div><div class="row tight"><input type="number" class="hm-apx" placeholder="X" style="width:72px"><input type="number" class="hm-apy" placeholder="Y" style="width:72px"><button type="button" class="btn sm secondary hm-apadd">${esc(T('hunt_add_row'))}</button></div></div>`
      + `</details>`
      + `<div class="hm-canvas-wrap" hidden></div>`
      + `</div>`;
  }
  function miniNum(cls, l, v) { return `<div class="hf-field hf-subf hf-leaf"><label class="label">${esc(T(l))}</label><div class="hf-num"><input type="number" class="${cls}" value="${v == null ? '' : esc(v)}"></div></div>`; }
  function xyChips(arr, del, numbered) { return (arr || []).map((p, j) => `<span class="chip" data-j="${j}"><span class="hf-chip-lab">${numbered ? (j + 1) + ': ' : ''}(${esc(p.x)}, ${esc(p.y)})</span><button type="button" class="${del}" data-j="${j}" aria-label="×">✕</button></span>`).join(''); }
  function avoidChips(arr) { return xyChips(arr, 'hm-avdel'); }
  function segChips(arr) { return xyChips(arr, 'hm-ptdel', true); }
  function approachChips(arr) { return xyChips(arr, 'hm-apdel'); }

  // ────────────────────────────────────────────── collect / read ──────────────────────────────────────────────
  function fieldLabel(field) { return T(field.l); }
  function readLeaf(el, field, path, errors) {
    const setErr = (msg) => { const e = el.querySelector(':scope > .error') || el.querySelector('.error'); if (e) e.textContent = msg; el.classList.add('invalid'); errors.push({ path, label: fieldLabel(field), message: msg }); };
    const clearErr = () => { const e = el.querySelector('.error'); if (e) e.textContent = ''; el.classList.remove('invalid'); };
    clearErr();
    if (field.type === 'bool') { const c = el.querySelector('input[type=checkbox]').checked; return (field.def != null && c === field.def) ? undefined : c; }
    if (field.type === 'num') {
      const raw = el.querySelector('input').value.trim();
      if (!raw) return undefined;
      const n = Number(raw);
      if (!Number.isFinite(n)) { setErr(T('hunt_err_num')); return undefined; }
      if (field.int && !Number.isInteger(n)) { setErr(T('hunt_err_int')); return undefined; }
      if (field.min != null && n < field.min) { setErr(T('hunt_err_min', field.min)); return undefined; }
      if (field.max != null && n > field.max) { setErr(T('hunt_err_max', field.max)); return undefined; }
      return n;
    }
    if (field.type === 'text') { const raw = el.querySelector('input').value.trim(); return raw || undefined; }
    if (field.type === 'select') { const v = el.querySelector('select').value; return v || undefined; }
    if (field.type === 'picker') return P.read(el.querySelector('.pick'));
    if (field.type === 'chips') { const a = chipValues(el, field.kind); return a.length ? a : undefined; }
    if (field.type === 'tags') { const a = chipValues(el, 'tag'); return a.length ? a : undefined; }
    return undefined;
  }
  function subFields(scope) { return [...scope.querySelectorAll('.hf-field')].filter(el => el.closest('.hf-sub') === scope); }
  function readContainer(scope, fields, baseObj, path, errors) {
    const obj = isObj(baseObj) ? clone(baseObj) : {};
    const direct = subFields(scope);
    const byKey = {};
    for (const el of direct) if (el.dataset.sk != null) byKey[el.dataset.sk] = el;
    const walk = (list) => {
      for (const f of list) {
        if (f.adv) { walk(f.fields); continue; }
        const el = byKey[f.k];
        if (!el) continue;
        const v = readFieldValue(el, f, obj[f.k], path + '.' + f.k, errors);
        if (v === undefined) delete obj[f.k]; else obj[f.k] = v;
      }
    };
    walk(fields);
    return Object.keys(obj).length ? obj : undefined;
  }
  function readFieldValue(el, field, baseVal, path, errors) {
    if (field.type === 'group') { const scope = el.querySelector(':scope > .hf-sub'); return readContainer(scope, field.fields, baseVal, path, errors); }
    if (field.type === 'travel') { const scope = el.querySelector(':scope > .hf-travel-body'); return readContainer(scope, travelFields(field.variant), baseVal, path, errors); }
    if (field.type === 'rows') return readRows(el, field);
    if (field.type === 'map') return readMap(el, field, path, errors);
    if (field.type === 'buffsByMap') return readBuffsByMap(el, path, errors);
    if (field.type === 'huntMaps') return readHuntMaps(el, baseVal, path, errors);
    return readLeaf(el, field, path, errors);
  }
  // A row's value is the JSON the row editor saved (unknown keys of the original row are kept by readContainer).
  function readRows(el, field) {
    const out = [];
    el.querySelectorAll(':scope > .hf-rows > .hf-row').forEach(row => {
      let obj = {};
      try { obj = JSON.parse(row.dataset.value || '{}'); } catch (_) { obj = {}; }
      if (!field.keep || field.keep(obj)) out.push(obj);
    });
    return out.length ? out : undefined;
  }
  function readMap(el, field, path, errors) {
    const rows = [...el.querySelectorAll(':scope > .hf-rows > .hf-row')];
    const dict = {};
    rows.forEach(row => {
      const key = P.raw(row.querySelector('.pick'));
      if (key === '' || key == null) return;
      const vin = row.querySelector('.hf-mapval');
      const raw = vin ? vin.value.trim() : '';
      if (raw === '') return;
      const n = Number(raw);
      if (!Number.isFinite(n)) { errors.push({ path: path + '.' + key, label: fieldLabel(field), message: T('hunt_err_num') }); row.querySelector('.hf-mapval').classList.add('invalid'); return; }
      dict[key] = n;
    });
    return Object.keys(dict).length ? dict : undefined;
  }
  function readBuffsByMap(el, path, errors) {
    const rows = [...el.querySelectorAll(':scope > .hf-rows > .hf-row')];
    const dict = {};
    rows.forEach(row => {
      const key = P.raw(row.querySelector('.pick'));
      if (key === '' || key == null) return;
      const arr = [...row.querySelectorAll('.hf-chips > .chip')].map(c => P.cast('skill', c.dataset.v)).filter(v => v != null);
      if (arr.length) dict[String(key)] = arr;
    });
    return Object.keys(dict).length ? dict : undefined;
  }
  function readHuntMaps(el, baseArr, path, errors) {
    const rows = [...el.querySelectorAll('.hm-row')];
    const out = [];
    rows.forEach((row, i) => {
      const base = row.dataset.original ? JSON.parse(row.dataset.original) : {};
      const obj = clone(base);
      const numOf = (sel, int) => { const e = row.querySelector(sel); if (!e) return undefined; const raw = e.value.trim(); if (!raw) return undefined; const n = Number(raw); if (!Number.isFinite(n) || (int && !Number.isInteger(n))) { errors.push({ path: path + '[' + i + ']', label: T('hunt_l_hunt_maps'), message: T('hunt_err_int') }); e.classList.add('invalid'); return undefined; } e.classList.remove('invalid'); return n; };
      setOrDel(obj, 'min_level', numOf('.hm-min', true));
      setOrDel(obj, 'max_level', numOf('.hm-max', true));
      setOrDel(obj, 'map_id', P.read(row.querySelector('.pick[data-kind="map"]')));
      setOrDel(obj, 'region_id', P.read(row.querySelector('.pick[data-kind="region"]')));
      try { const pts = JSON.parse(row.querySelector('.hm-patrol').value || '[]'); setOrDel(obj, 'patrol_points', pts.length ? pts : undefined); } catch (_) {}
      try { const av = JSON.parse(row.querySelector('.hm-avoid').value || '[]'); setOrDel(obj, 'avoid_tiles', av.length ? av : undefined); } catch (_) {}
      try { const ap = JSON.parse(row.querySelector('.hm-approach').value || '[]'); setOrDel(obj, 'approach_points', ap.length ? ap : undefined); } catch (_) {}
      const entry = clone(base.entry) || {};
      setOrDel(entry, 'map_id', P.read(row.querySelector('.hm-entry .pick[data-kind="map"]')));
      setOrDel(entry, 'x', numOf('.hm-ex', true));
      setOrDel(entry, 'y', numOf('.hm-ey', true));
      const push = clone(base.entry && base.entry.push) || {};
      setOrDel(push, 'x', numOf('.hm-px', true));
      setOrDel(push, 'y', numOf('.hm-py', true));
      if (Object.keys(push).length) entry.push = push; else delete entry.push;
      if (Object.keys(entry).length) obj.entry = entry; else delete obj.entry;
      const keep = obj.map_id != null || (obj.patrol_points && obj.patrol_points.length) || obj.min_level != null || obj.max_level != null || obj.region_id != null || obj.entry || obj.avoid_tiles;
      if (keep) out.push(obj);
    });
    return out.length ? out : undefined;
  }
  function setOrDel(o, k, v) { if (v === undefined) delete o[k]; else o[k] = v; }

  // getPath/setPath/delPath over the output document
  const getPath = (o, p) => p.split('.').reduce((a, k) => (a == null ? undefined : a[k]), o);
  function setPath(o, p, v) { const ks = p.split('.'); let a = o; for (const k of ks.slice(0, -1)) a = a[k] = isObj(a[k]) ? a[k] : {}; a[ks[ks.length - 1]] = v; }
  function delPath(o, p) {
    const ks = p.split('.'); const stack = []; let a = o;
    for (const k of ks.slice(0, -1)) { if (!isObj(a)) return; stack.push([a, k]); a = a[k]; }
    if (isObj(a)) delete a[ks[ks.length - 1]];
    for (let i = stack.length - 1; i >= 0; i--) { const [par, k] = stack[i]; const v = par[k]; if (isObj(v) && !Object.keys(v).length) delete par[k]; else break; }
  }

  function collect(I) {
    const errors = [];
    const out = clone(I.base) || {};
    for (const field of ALL_FIELDS) {
      if (!I.touched.has(String(field._fid))) continue;
      const el = I.root.querySelector(`.hf-field[data-fid="${field._fid}"]`);
      if (!el) continue;
      const base = getPath(I.base, field.path);
      const v = readFieldValue(el, field, base, field.path, errors);
      if (v === undefined) delPath(out, field.path); else setPath(out, field.path, v);
    }
    if (errors.length) throw new ValidationError(errors);
    return out;
  }
  // non-throwing doc for JSON display + dirty diffing
  function currentDoc(I) {
    const scrap = [];
    const out = clone(I.base) || {};
    for (const field of ALL_FIELDS) {
      if (!I.touched.has(String(field._fid))) continue;
      const el = I.root.querySelector(`.hf-field[data-fid="${field._fid}"]`);
      if (!el) continue;
      let v;
      try { v = readFieldValue(el, field, getPath(I.base, field.path), field.path, scrap); } catch (_) { continue; }
      if (v === undefined) delPath(out, field.path); else setPath(out, field.path, v);
    }
    return out;
  }

  // ────────────────────────────────────────────── patrol canvas ──────────────────────────────────────────────
  function expandedRow(I) { return I.root.querySelector('.hm-row.hm-open'); }
  function rowMapId(row) { return P.raw(row.querySelector('.pick[data-kind="map"]')); }
  function rowRegionId(row) { return P.raw(row.querySelector('.pick[data-kind="region"]')); }
  function readPts(row) { try { return JSON.parse(row.querySelector('.hm-patrol').value || '[]'); } catch (_) { return []; } }
  function writePts(I, row, arr) {
    row.querySelector('.hm-patrol').value = JSON.stringify(arr);
    const btn = row.querySelector('.hm-toggle'); if (btn) btn.textContent = T('hunt_points_n', arr.length);
    const list = row.querySelector('.hm-ptlist'); if (list) list.innerHTML = segChips(arr);
    const cnt = row.querySelector('.hm-count'); if (cnt) cnt.textContent = T('hunt_points_n', arr.length);
    markTouched(I, row);
  }
  function pad(w, f) { const dx = (w.maxX - w.minX) * f, dy = (w.maxY - w.minY) * f; return { minX: w.minX - dx, minY: w.minY - dy, maxX: w.maxX + dx, maxY: w.maxY + dy }; }
  function gridInfo(row) { const mid = rowMapId(row); if (mid === '') return null; const g = App.cat.gridInfo(mid); return g ? { info: g, id: mid } : null; }
  function gridImg(I, id) { I.gimg = I.gimg || {}; if (!I.gimg[id]) { const img = new Image(); img.onload = () => drawCanvas(I); img.src = App.cat.asset('gridmap/' + id + '.png', 'gridmap'); I.gimg[id] = img; } return I.gimg[id]; }
  function patrolWindow(I, row) {
    const rid = rowRegionId(row);
    if (rid) { const rg = App.cat.regionById(rid); if (rg && rg.l != null) return pad({ minX: Math.min(rg.l, rg.r), minY: Math.min(rg.t, rg.b), maxX: Math.max(rg.l, rg.r), maxY: Math.max(rg.t, rg.b) }, 0.05); }
    const pg = gridInfo(row); if (pg) { const i = pg.info; return pad({ minX: i.ox, minY: i.oy, maxX: i.ox + i.w, maxY: i.oy + i.h }, 0.02); }
    const pts = readPts(row);
    if (pts.length) { let a = 1e12, b = 1e12, c = -1e12, d = -1e12; for (const p of pts) { a = Math.min(a, p.x); b = Math.min(b, p.y); c = Math.max(c, p.x); d = Math.max(d, p.y); } if (c - a < 400) { a -= 500; c += 500; } if (d - b < 400) { b -= 500; d += 500; } return pad({ minX: a, minY: b, maxX: c, maxY: d }, 0.15); }
    return { minX: 31000, minY: 31000, maxX: 33000, maxY: 33000 };
  }
  function ensureView(I, cvs, row) {
    if (!I.pview) { const w = patrolWindow(I, row); const sx = cvs.width / ((w.maxX - w.minX) || 1), sy = cvs.height / ((w.maxY - w.minY) || 1); I.pview = { cx: (w.minX + w.maxX) / 2, cy: (w.minY + w.maxY) / 2, scale: Math.min(sx, sy) * 0.95 || 0.1 }; }
    return I.pview;
  }
  function drawCanvas(I) {
    const row = expandedRow(I); if (!row) return;
    const cvs = row.querySelector('.hm-canvas'); if (!cvs) return;
    const g = cvs.getContext('2d'); const W = cvs.width, H = cvs.height;
    const view = ensureView(I, cvs, row);
    const w2s = (wx, wy) => ({ x: W / 2 + (wx - view.cx) * view.scale, y: H / 2 + (wy - view.cy) * view.scale });
    g.clearRect(0, 0, W, H); g.fillStyle = '#05070a'; g.fillRect(0, 0, W, H);
    const pg = gridInfo(row);
    if (pg) { const img = gridImg(I, pg.id); if (img && img.complete && img.naturalWidth) { const tl = w2s(pg.info.ox, pg.info.oy); g.imageSmoothingEnabled = false; try { g.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, tl.x, tl.y, img.naturalWidth * view.scale, img.naturalHeight * view.scale); } catch (_) {} } }
    const vMinX = view.cx - (W / 2) / view.scale, vMaxX = view.cx + (W / 2) / view.scale, vMinY = view.cy - (H / 2) / view.scale, vMaxY = view.cy + (H / 2) / view.scale;
    let step = 100; while ((vMaxX - vMinX) / step > 16) step *= 2; while ((vMaxX - vMinX) / step < 5) step /= 2; if (step < 1) step = 1;
    g.strokeStyle = '#141b2488'; g.lineWidth = 1;
    for (let gx = Math.ceil(vMinX / step) * step; gx <= vMaxX; gx += step) { const x = w2s(gx, 0).x; g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke(); }
    for (let gy = Math.ceil(vMinY / step) * step; gy <= vMaxY; gy += step) { const y = w2s(0, gy).y; g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke(); }
    const pts = readPts(row);
    if (pts.length > 1) { g.strokeStyle = '#3b82f6aa'; g.lineWidth = 1.5; g.beginPath(); pts.forEach((p, i) => { const q = w2s(p.x, p.y); i === 0 ? g.moveTo(q.x, q.y) : g.lineTo(q.x, q.y); }); g.stroke(); }
    pts.forEach((p, i) => { const q = w2s(p.x, p.y); g.fillStyle = '#3b82f6'; g.beginPath(); g.arc(q.x, q.y, 8, 0, 7); g.fill(); g.fillStyle = '#fff'; g.font = '10px sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), q.x, q.y); });
    if (I.liveFn) { try { const p = I.liveFn(); if (p) I.livePos = { map_id: p.map_id, x: p.x, y: p.y }; } catch (_) {} }
    const lv = I.livePos;
    if (lv && lv.x != null && lv.y != null && String(rowMapId(row)) === String(lv.map_id)) {
      const me = w2s(lv.x, lv.y);
      g.strokeStyle = '#fff'; g.lineWidth = 2; g.fillStyle = '#3fb950';
      g.beginPath(); g.arc(me.x, me.y, 6, 0, 7); g.fill(); g.stroke();
      g.font = 'bold 11px sans-serif'; g.textAlign = 'left'; g.textBaseline = 'bottom'; g.lineWidth = 3; g.strokeStyle = '#05070a';
      const label = T('hunt_me') + ' (' + lv.x + ',' + lv.y + ')';
      g.strokeText(label, me.x + 8, me.y - 4); g.fillStyle = '#3fb950'; g.fillText(label, me.x + 8, me.y - 4);
    }
  }
  function openCanvas(I, row) {
    I.root.querySelectorAll('.hm-row.hm-open').forEach(r => { if (r !== row) { r.classList.remove('hm-open'); const w = r.querySelector('.hm-canvas-wrap'); if (w) { w.hidden = true; w.innerHTML = ''; } } });
    const wrap = row.querySelector('.hm-canvas-wrap');
    const pts = readPts(row);
    wrap.innerHTML = `<div class="small muted hf-canvas-hint">${esc(T('hunt_canvas_hint'))}</div>`
      + `<canvas class="hm-canvas" width="620" height="380"></canvas>`
      + `<div class="row between"><div class="row tight"><button type="button" class="btn sm secondary hm-here">${esc(T('hunt_here'))}</button><button type="button" class="btn sm ghost hm-clear">${esc(T('hunt_clear_points'))}</button></div><span class="small muted hm-count">${esc(T('hunt_points_n', pts.length))}</span></div>`
      + `<div class="hf-chips hm-ptlist">${segChips(pts)}</div>`;
    wrap.hidden = false;
    row.classList.add('hm-open');
    I.pview = null;
    wireCanvas(I, row);
    drawCanvas(I);
    clearInterval(I.liveTimer);
    if (I.liveFn) I.liveTimer = setInterval(() => { if (expandedRow(I)) drawCanvas(I); else { clearInterval(I.liveTimer); I.liveTimer = null; } }, 1500);
  }
  function closeCanvas(I, row) { row.classList.remove('hm-open'); const w = row.querySelector('.hm-canvas-wrap'); if (w) { w.hidden = true; w.innerHTML = ''; } clearInterval(I.liveTimer); I.liveTimer = null; }
  function wireCanvas(I, row) {
    const cvs = row.querySelector('.hm-canvas'); if (!cvs) return;
    const toC = (e) => { const rc = cvs.getBoundingClientRect(); return { x: (e.clientX - rc.left) / rc.width * cvs.width, y: (e.clientY - rc.top) / rc.height * cvs.height }; };
    const toWorld = (c) => ({ x: Math.round(I.pview.cx + (c.x - cvs.width / 2) / I.pview.scale), y: Math.round(I.pview.cy + (c.y - cvs.height / 2) / I.pview.scale) });
    let drag = null;
    cvs.onmousedown = (e) => {
      const c = toC(e); const w2s = (p) => ({ x: cvs.width / 2 + (p.x - I.pview.cx) * I.pview.scale, y: cvs.height / 2 + (p.y - I.pview.cy) * I.pview.scale });
      const pts = readPts(row); let hit = -1;
      pts.forEach((p, i) => { const s = w2s(p); if (Math.hypot(s.x - c.x, s.y - c.y) <= 9) hit = i; });
      drag = { x: c.x, y: c.y, moved: false, cx: I.pview.cx, cy: I.pview.cy, pt: hit };
    };
    cvs.onmousemove = (e) => {
      if (!drag) return; const c = toC(e); const dx = c.x - drag.x, dy = c.y - drag.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.moved = true;
      if (drag.pt >= 0) { const arr = readPts(row); const w = toWorld(c); arr[drag.pt] = w; writePts(I, row, arr); drawCanvas(I); }
      else { I.pview.cx = drag.cx - dx / I.pview.scale; I.pview.cy = drag.cy - dy / I.pview.scale; drawCanvas(I); }
    };
    cvs.onmouseup = (e) => { if (drag && !drag.moved && drag.pt < 0) { const arr = readPts(row); arr.push(toWorld(toC(e))); writePts(I, row, arr); drawCanvas(I); } drag = null; };
    cvs.onmouseleave = () => { drag = null; };
    cvs.onwheel = (e) => { e.preventDefault(); const c = toC(e); const v = I.pview; const wx = v.cx + (c.x - cvs.width / 2) / v.scale, wy = v.cy + (c.y - cvs.height / 2) / v.scale; v.scale *= (e.deltaY < 0 ? 1.2 : 1 / 1.2); v.cx = wx - (c.x - cvs.width / 2) / v.scale; v.cy = wy - (c.y - cvs.height / 2) / v.scale; drawCanvas(I); };
  }

  // ────────────────────────────────────────────── events ──────────────────────────────────────────────
  function ownerField(I, el) { const f = el.closest('.hf-field[data-fid]'); return f; }
  function markTouched(I, el) {
    const f = el && el.closest ? el.closest('.hf-field[data-fid]') : null;
    if (!f) return;
    I.touched.add(f.dataset.fid);
    f.classList.add('is-touched');
    if (I.onChange) { try { I.onChange(dirtyCount(I)); } catch (_) {} }
  }
  function addChip(I, chipsField, kind, v) {
    if (v === '' || v == null) return;
    const list = chipsField.querySelector('.hf-chips');
    if ([...list.querySelectorAll('.chip')].some(c => c.dataset.v === String(v))) return;
    list.insertAdjacentHTML('beforeend', chipHtml(kind, v));
    markTouched(I, chipsField);
  }
  function applyShowIf(I) {
    I.root.querySelectorAll('[data-showif]').forEach(el => {
      const [k, vals] = el.dataset.showif.split(':'); const allow = vals.split(',');
      const row = el.closest('.hf-row') || el.closest('.hf-sub');
      const ctrl = row && row.querySelector(`.hf-field[data-sk="${k}"] select`);
      const cur = ctrl ? ctrl.value : '';
      el.classList.toggle('hidden', !allow.includes(cur));
    });
  }
  function filterSearch(I, q) {
    q = (q || '').trim().toLowerCase();
    let any = false;
    I.root.querySelectorAll('.hf-section').forEach(sec => {
      if (sec.dataset.secid === 'json') { sec.classList.toggle('hidden', !!q); return; }
      let visible = 0;
      sec.querySelectorAll('.hf-field[data-s]').forEach(f => {
        const hit = !q || (f.dataset.s || '').includes(q);
        f.classList.toggle('hidden', !hit);
        if (hit) visible++;
      });
      sec.classList.toggle('hidden', q && !visible);
      // keep collapsible blocks open while searching so matches are visible
      if (q) sec.querySelectorAll('details.hf-block').forEach(d => { d.open = true; });
      if (visible) any = true;
    });
    const nores = I.root.querySelector('.hf-nores'); if (nores) nores.hidden = !(q && !any);
    I.root.querySelectorAll('.hf-navlink').forEach(a => { const sec = I.root.querySelector(`.hf-section[data-secid="${a.dataset.sec}"]`); a.classList.toggle('hidden', !!(sec && sec.classList.contains('hidden'))); });
  }

  function bindEvents(I) {
    const root = I.root;
    const readonly = I.readOnly;
    const onSelect = (pick, value) => {
      if (pick.classList.contains('pick-chipadd')) { const cf = pick.closest('.hf-chipsf') || pick.closest('.hf-field'); addChip(I, cf, pick.dataset.kind, value); return; }
      markTouched(I, pick);
      const hmRow = pick.closest('.hm-row');
      if (hmRow && hmRow.classList.contains('hm-open') && !pick.closest('.hm-entry')) { I.pview = null; drawCanvas(I); }
    };
    const onType = (pick) => markTouched(I, pick);
    I.unbindPicker = P.bind(root, { readOnly: readonly, onSelect, onType });
    root.addEventListener('submit', e => e.preventDefault());
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && I.modal && !e.target.classList.contains('pick-in')) { e.preventDefault(); closeRowModal(I); }
    });
    if (!readonly) {
    root.addEventListener('input', (e) => {
      const t = e.target;
      if (t.classList.contains('pick-in')) return; // App.picker owns catalog inputs
      if (t.classList.contains('hf-search')) { filterSearch(I, t.value); return; }
      markTouched(I, t);
    });
    root.addEventListener('change', (e) => { if (!e.target.classList.contains('pick-in')) { markTouched(I, e.target); if (e.target.tagName === 'SELECT') applyShowIf(I); } });
    root.addEventListener('keydown', (e) => {
      if (!e.target.classList.contains('hf-taginput')) return;
      if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); const v = e.target.value.trim().replace(/,$/, ''); if (v) { addChip(I, e.target.closest('.hf-field'), 'tag', v); e.target.value = ''; } }
    });
    }
    root.addEventListener('click', (e) => {
      const t = e.target;
      const nav = t.closest('.hf-navlink'); if (nav) { e.preventDefault(); const sec = I.root.querySelector(`.hf-section[data-secid="${nav.dataset.sec}"]`); if (sec) sec.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      const tw = t.closest('.hf-travel-head'); if (tw) { tw.parentElement.classList.toggle('open'); const body = tw.nextElementSibling; body.hidden = !tw.parentElement.classList.contains('open'); return; }
      // Row editor: a click on a row (or its backdrop / buttons) — viewing works read-only too.
      if (I.modal) {
        if (t.closest('.hf-modal-save')) { saveRowModal(I); return; }
        if (t.closest('.hf-modal-cancel') || t === I.modal.wrap) { closeRowModal(I); return; }
      }
      const rowEdit = t.closest('.hf-rowedit'); if (rowEdit) { openRowModal(I, rowEdit.closest('[data-ctl]'), rowEdit.closest('.hf-row')); return; }
      if (readonly) return;
      const rowAdd = t.closest('.hf-rowadd'); if (rowAdd) { openRowModal(I, rowAdd.closest('[data-ctl]'), null); return; }
      const mapAdd = t.closest('.hf-mapadd'); if (mapAdd) { const cf = mapAdd.closest('[data-ctl]'); const c = I.ctl.get(cf.dataset.ctl); const html = c.kind === 'buffsByMap' ? buffMapRowHtml(c.field, '', []) : mapRowHtml(c.field, '', ''); cf.querySelector('.hf-rows').insertAdjacentHTML('beforeend', html); markTouched(I, cf); return; }
      const rowDel = t.closest('.hf-rowdel'); if (rowDel) { const cf = rowDel.closest('.hf-field[data-fid]'); const list = rowDel.closest('.hf-rows'); rowDel.closest('.hf-row').remove(); const empty = list && list.parentElement.querySelector(':scope > .hf-rows-empty'); if (empty) empty.hidden = list.children.length > 0; markTouched(I, cf); return; }
      const chipDel = t.closest('.hf-chipdel'); if (chipDel) { const cf = chipDel.closest('.hf-field[data-fid]'); chipDel.closest('.chip').remove(); markTouched(I, cf); return; }
      // hunt maps
      const hmAdd = t.closest('.hm-add'); if (hmAdd) { const field = ALL_FIELDS.find(f => f.type === 'huntMaps'); const maps = hmAdd.closest('.hf-huntmaps').querySelector('.hf-maps'); maps.insertAdjacentHTML('beforeend', huntMapRow({}, maps.children.length)); markTouched(I, hmAdd.closest('.hf-field[data-fid]')); return; }
      const hmDel = t.closest('.hm-del'); if (hmDel) { const cf = hmDel.closest('.hf-field[data-fid]'); hmDel.closest('.hm-row').remove(); markTouched(I, cf); return; }
      const hmTog = t.closest('.hm-toggle'); if (hmTog) { const row = hmTog.closest('.hm-row'); if (row.classList.contains('hm-open')) closeCanvas(I, row); else { if (rowMapId(row) === '' && !readPts(row).length) App.toast(T('hunt_map_pick_first'), 'info'); openCanvas(I, row); } return; }
      const hmHere = t.closest('.hm-here'); if (hmHere) { const row = hmHere.closest('.hm-row'); const lv = I.liveFn ? (I.liveFn() || I.livePos) : I.livePos; if (lv && String(lv.map_id) === String(rowMapId(row)) && lv.x != null) { const arr = readPts(row); arr.push({ x: lv.x, y: lv.y }); writePts(I, row, arr); drawCanvas(I); } else App.toast(T('hunt_not_on_map'), 'info'); return; }
      const hmClear = t.closest('.hm-clear'); if (hmClear) { const row = hmClear.closest('.hm-row'); writePts(I, row, []); drawCanvas(I); return; }
      const ptDel = t.closest('.hm-ptdel'); if (ptDel) { const row = ptDel.closest('.hm-row'); const arr = readPts(row); arr.splice(Number(ptDel.dataset.j), 1); writePts(I, row, arr); drawCanvas(I); return; }
      const apAdd = t.closest('.hm-apadd'); if (apAdd) { const det = apAdd.closest('.hm-entry'); const xi = det.querySelector('.hm-apx'), yi = det.querySelector('.hm-apy'); const x = Number(xi.value), y = Number(yi.value); if (Number.isInteger(x) && Number.isInteger(y)) { const hid = det.querySelector('.hm-approach'); const arr = JSON.parse(hid.value || '[]'); arr.push({ x, y }); hid.value = JSON.stringify(arr); det.querySelector('.hf-approach-list').innerHTML = approachChips(arr); xi.value = ''; yi.value = ''; markTouched(I, apAdd.closest('.hf-field[data-fid]')); } return; }
      const apDel = t.closest('.hm-apdel'); if (apDel) { const det = apDel.closest('.hm-entry'); const hid = det.querySelector('.hm-approach'); const arr = JSON.parse(hid.value || '[]'); arr.splice(Number(apDel.dataset.j), 1); hid.value = JSON.stringify(arr); det.querySelector('.hf-approach-list').innerHTML = approachChips(arr); markTouched(I, apDel.closest('.hf-field[data-fid]')); return; }
      const avAdd = t.closest('.hm-avadd'); if (avAdd) { const det = avAdd.closest('.hm-entry'); const xi = det.querySelector('.hm-avx'), yi = det.querySelector('.hm-avy'); const x = Number(xi.value), y = Number(yi.value); if (Number.isInteger(x) && Number.isInteger(y)) { const hid = det.querySelector('.hm-avoid'); const arr = JSON.parse(hid.value || '[]'); arr.push({ x, y }); hid.value = JSON.stringify(arr); det.querySelector('.hf-avoid-list').innerHTML = avoidChips(arr); xi.value = ''; yi.value = ''; markTouched(I, avAdd.closest('.hf-field[data-fid]')); } return; }
      const avDel = t.closest('.hm-avdel'); if (avDel) { const det = avDel.closest('.hm-entry'); const hid = det.querySelector('.hm-avoid'); const arr = JSON.parse(hid.value || '[]'); arr.splice(Number(avDel.dataset.j), 1); hid.value = JSON.stringify(arr); det.querySelector('.hf-avoid-list').innerHTML = avoidChips(arr); markTouched(I, avDel.closest('.hf-field[data-fid]')); return; }
      // JSON
      const jApply = t.closest('.hf-json-apply'); if (jApply) { applyJson(I); return; }
      const jRefresh = t.closest('.hf-json-refresh'); if (jRefresh) { const ta = I.root.querySelector('.hf-json-area'); ta.value = JSON.stringify(currentDoc(I), null, 2); const er = I.root.querySelector('.hf-json-err'); if (er) er.textContent = ''; return; }
    });
  }

  function applyJson(I) {
    const ta = I.root.querySelector('.hf-json-area'); const er = I.root.querySelector('.hf-json-err');
    let parsed; try { parsed = JSON.parse(ta.value); } catch (_) { er.textContent = T('hunt_err_json'); return; }
    if (!isObj(parsed)) { er.textContent = T('hunt_err_json_obj'); return; }
    er.textContent = '';
    I.base = parsed; I.touched.clear(); I.jsonReplaced = true;
    renderBody(I);
    if (I.onChange) { try { I.onChange(dirtyCount(I)); } catch (_) {} }
    App.toast(T('hunt_json_applied'));
  }

  function dirtyCount(I) {
    if (I.jsonReplaced) { try { return Math.max(1, leafDiff(currentDoc(I), I.server)); } catch (_) { return 1; } }
    return I.touched.size;
  }
  function leafDiff(a, b) {
    // count of leaf paths that differ between two docs
    const paths = new Set();
    const walk = (o, pre) => { if (isObj(o)) { for (const k in o) walk(o[k], pre + '.' + k); } else paths.add(pre + '=' + JSON.stringify(o)); };
    const A = new Set(); const B = new Set();
    (function w(o, p, set) { if (isObj(o)) { for (const k in o) w(o[k], p + '.' + k, set); } else set.add(p + '=' + JSON.stringify(o)); })(a || {}, '', A);
    (function w(o, p, set) { if (isObj(o)) { for (const k in o) w(o[k], p + '.' + k, set); } else set.add(p + '=' + JSON.stringify(o)); })(b || {}, '', B);
    let n = 0; A.forEach(x => { if (!B.has(x)) n++; }); B.forEach(x => { if (!A.has(x)) n++; });
    return Math.ceil(n / 1);
  }

  // ────────────────────────────────────────────── body render ──────────────────────────────────────────────
  function renderBody(I) {
    I.ctl = new Map(); I.uidc = 0; I.pview = null;
    const nav = SECTIONS.map(s => `<a href="#" class="hf-navlink" data-sec="${s.id}"><span class="hf-navico">${s.icon}</span><span>${esc(T(s.l))}</span></a>`).join('');
    let body = '';
    for (const sec of SECTIONS) {
      body += `<section class="hf-section" data-secid="${sec.id}"><div class="hf-sec-head"><span class="hf-navico">${sec.icon}</span><h3>${esc(T(sec.l))}</h3></div>`;
      if (sec.d) body += `<p class="desc small muted">${esc(T(sec.d))}</p>`;
      for (const blk of sec.blocks) {
        if (blk.custom === 'json') { body += renderJsonBlock(I); continue; }
        const inner = (blk.fields || []).map(f => renderField(I, f, getPath(I.base, f.path), `data-fid="${f._fid}"`, sec.showKey || blk.showKey)).join('');
        if (blk.l && blk.collapsible) body += `<details class="hf-block"><summary>${esc(T(blk.l))}</summary><div class="hf-fields">${inner}</div></details>`;
        else if (blk.l) body += `<div class="hf-block-flat"><div class="hf-block-l">${esc(T(blk.l))}</div><div class="hf-fields">${inner}</div></div>`;
        else body += `<div class="hf-fields">${inner}</div>`;
      }
      body += `</section>`;
    }
    I.root.innerHTML = `<div class="hf${I.readOnly ? ' hf-ro' : ''}">`
      + `<aside class="hf-nav"><div class="hf-searchwrap"><input type="search" class="hf-search" placeholder="${esc(T('hunt_search_ph'))}" aria-label="${esc(T('hunt_search_ph'))}"${I.readOnly ? ' disabled' : ''}></div><nav class="hf-navlist">${nav}</nav></aside>`
      + `<div class="hf-body">${body}<div class="empty-state hf-nores" hidden><p>${esc(T('hunt_no_results'))}</p></div></div>`
      + `</div>`;
    I.modal = null;
    if (I.readOnly) I.root.querySelectorAll('input,select,textarea,button').forEach(el => { if (!el.closest('.hf-nav') && !el.closest('.hf-travel-head') && !el.classList.contains('hf-rowedit')) el.disabled = true; });
    applyShowIf(I);
    setupObserver(I);
  }
  function renderJsonBlock(I) {
    return `<div class="hf-jsonblk"><p class="hint">${esc(T('hunt_json_hint'))}</p>`
      + `<textarea class="hf-json-area" rows="24" spellcheck="false"${I.readOnly ? ' readonly' : ''}>${esc(JSON.stringify(currentDoc(I), null, 2))}</textarea>`
      + `<div class="hf-json-err error"></div>`
      + (I.readOnly ? '' : `<div class="row"><button type="button" class="btn secondary hf-json-apply">${esc(T('hunt_json_apply'))}</button><button type="button" class="btn ghost hf-json-refresh">${esc(T('hunt_json_refresh'))}</button></div>`)
      + `</div>`;
  }
  function setupObserver(I) {
    if (I.obs) I.obs.disconnect();
    const links = [...I.root.querySelectorAll('.hf-navlink')];
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { const id = en.target.dataset.secid; links.forEach(a => a.classList.toggle('on', a.dataset.sec === id)); } });
    }, { rootMargin: '-10% 0px -80% 0px', threshold: 0 });
    I.root.querySelectorAll('.hf-section').forEach(s => io.observe(s));
    I.obs = io;
    // refresh JSON textarea when it scrolls into focus
    const ta = I.root.querySelector('.hf-json-area');
    if (ta && !I.readOnly) ta.addEventListener('focus', () => { ta.value = JSON.stringify(currentDoc(I), null, 2); });
  }

  // ────────────────────────────────────────────── public: create ──────────────────────────────────────────────
  function create(container, opts) {
    opts = opts || {};
    const I = {
      root: container, readOnly: !!opts.readOnly, onChange: opts.onChange || null, liveFn: opts.live || null,
      livePos: null, server: clone(opts.settings) || {}, base: clone(opts.settings) || {},
      touched: new Set(), jsonReplaced: false, ctl: new Map(), uidc: 0, gimg: {}
    };
    renderBody(I);
    bindEvents(I);
    if (I.liveFn) { try { I.livePos = I.liveFn() || null; } catch (_) {} }
    const api = {
      collect() { return collect(I); },
      isDirty() { return I.jsonReplaced || I.touched.size > 0; },
      dirtyCount() { return dirtyCount(I); },
      reset(settings) {
        I.server = clone(settings) || {}; I.base = clone(settings) || {}; I.touched.clear(); I.jsonReplaced = false;
        renderBody(I);
        if (I.onChange) { try { I.onChange(0); } catch (_) {} }
      },
      setLive(pos) { I.livePos = pos ? { map_id: pos.map_id, x: pos.x, y: pos.y } : null; if (expandedRow(I)) drawCanvas(I); },
      destroy() { if (I.obs) I.obs.disconnect(); if (I.unbindPicker) I.unbindPicker(); clearInterval(I.liveTimer); I.liveTimer = null; I.root.innerHTML = ''; }
    };
    I.api = api;
    return api;
  }

  // ────────────────────────────────────────────── public: mountSlotEditor ──────────────────────────────────────────────
  function mountSlotEditor(container, opts) {
    opts = opts || {};
    const slotId = opts.slotId;
    const state = { st: null, mode: null, templateId: null, form: null, revision: null, liveFn: opts.live || null, destroyed: false, saving: false, customDraft: null };
    let unsubWs = null;

    function isDirty() {
      if (!state.st) return false;
      if (state.mode !== state.st.mode) return true;
      if (state.mode === 'template' && Number(state.templateId) !== Number(state.st.template_id)) return true;
      if (state.mode === 'custom' && state.form) return state.form.isDirty() || state.customDraft != null;
      return false;
    }
    function updateGuard() { App.setLeaveGuard(() => !state.destroyed && isDirty()); }

    function templateSettings(id) { const tp = (state.st.templates || []).find(t => Number(t.id) === Number(id)); return tp ? (tp.settings || {}) : {}; }
    function defaultTemplate() {
      const tps = state.st.templates || [];
      const own = tps.find(t => t.is_default);
      if (own) return own;
      const pid = state.st.platform_default_id;
      if (pid != null) { const pf = tps.find(t => Number(t.id) === Number(pid)); if (pf) return pf; }
      return null;
    }
    function appliedDefault() {
      const st = state.st;
      if (state.mode === st.mode) {
        if (st.default_source === 'user') return { source: 'user', name: st.default_name || '' };
        if (st.default_source === 'platform') return { source: 'platform', name: st.default_name || '' };
        return null;
      }
      const own = (st.templates || []).find(t => t.is_default);
      if (own) return { source: 'user', name: own.name };
      const pid = st.platform_default_id;
      if (pid != null) { const pf = (st.templates || []).find(t => Number(t.id) === Number(pid)); if (pf) return { source: 'platform', name: pf.name }; }
      return null;
    }

    function render() {
      if (state.destroyed) return;
      const st = state.st;
      container.innerHTML = `<div class="hf-slot">
        <div class="hf-modebar">
          <div class="seg hf-modeseg" role="tablist">
            <button type="button" data-mode="custom" class="${state.mode === 'custom' ? 'on' : ''}">${esc(T('hunt_mode_custom'))}</button>
            <button type="button" data-mode="template" class="${state.mode === 'template' ? 'on' : ''}">${esc(T('hunt_mode_template'))}</button>
            <button type="button" data-mode="default" class="${state.mode === 'default' ? 'on' : ''}">${esc(T('hunt_mode_default'))}</button>
          </div>
          <div class="hf-modemeta grow">${modeMeta()}</div>
        </div>
        <div class="hf-formhost"></div>
        <div class="hf-savebar" hidden>
          <span class="hf-savemsg"></span>
          <div class="row tight">
            <button type="button" class="btn secondary hf-revert">${esc(T('hunt_revert'))}</button>
            <button type="button" class="btn primary hf-save">${esc(T('save'))}</button>
          </div>
        </div>
      </div>`;
      container.querySelectorAll('.hf-modeseg button').forEach(b => b.onclick = () => onMode(b.dataset.mode));
      container.querySelector('.hf-revert').onclick = onRevert;
      container.querySelector('.hf-save').onclick = onSave;
      mountForm();
      updateSaveBar();
      updateGuard();
    }
    function modeMeta() {
      const online = state.st.agent_online;
      const dot = `<span class="dot ${online ? 'ok' : ''}"></span>${esc(T(online ? 'hunt_agent_online' : 'hunt_agent_offline'))}`;
      let hint = state.mode === 'custom' ? T('hunt_hint_custom') : state.mode === 'template' ? T('hunt_hint_template') : T('hunt_default_chain');
      let sel = '';
      if (state.mode === 'template') {
        const tps = state.st.templates || [];
        if (tps.length) {
          const own = tps.filter(t => !t.shared);
          const shared = tps.filter(t => t.shared);
          const opt = t => `<option value="${t.id}"${Number(t.id) === Number(state.templateId) ? ' selected' : ''}>${esc(t.name)}${t.is_default ? ' ★' : ''}${t.shared && t.owner ? ' · ' + esc(t.owner) : ''}</option>`;
          let groups = '';
          if (own.length) groups += `<optgroup label="${esc(T('hunt_own_group'))}">${own.map(opt).join('')}</optgroup>`;
          if (shared.length) groups += `<optgroup label="${esc(T('hunt_shared_group'))}">${shared.map(opt).join('')}</optgroup>`;
          sel = `<label class="hf-tplsel"><span class="small muted">${esc(T('hunt_pick_template'))}</span><select class="hf-tpl">${groups}</select></label>`;
        } else {
          sel = `<span class="small muted">${esc(T('hunt_no_template'))}</span>`;
        }
      }
      let applied = '';
      if (state.mode === 'default') {
        const info = appliedDefault();
        applied = info
          ? `<span class="small muted hf-applied">${esc(T(info.source === 'platform' ? 'hunt_default_applied_platform' : 'hunt_default_applied_user', info.name))}</span>`
          : `<span class="badge warn hf-applied">${esc(T('hunt_default_none_warn'))}</span>`;
      }
      let edit = '';
      if (state.mode !== 'custom') edit = `<button type="button" class="btn sm secondary hf-editmine">${esc(T('hunt_edit_here'))}</button>`;
      return `<span class="hf-conn small muted">${dot}</span><span class="small muted hf-modehint">${esc(hint)}</span>${applied}${sel}${edit}`;
    }
    function previewSettings() {
      if (state.mode === 'template') return templateSettings(state.templateId);
      if (state.mode === 'default') {
        if (state.mode === state.st.mode) return state.st.settings || {};
        const d = defaultTemplate(); return d ? (d.settings || {}) : {};
      }
      return state.st.custom || state.st.settings || {};
    }
    function mountForm() {
      const host = container.querySelector('.hf-formhost');
      if (state.form) { state.form.destroy(); state.form = null; }
      if (state.mode === 'custom') {
        host.innerHTML = '';
        const seed = state.customDraft != null ? state.customDraft : (state.st.custom || state.st.settings || {});
        state.form = create(host, { settings: seed, live: state.liveFn, onChange: () => { updateSaveBar(); updateGuard(); } });
      } else {
        const prev = previewSettings();
        const warn = (state.mode === 'default' && Object.keys(prev).length === 0) ? `<div class="banner warn hf-emptywarn">⚠ <span>${esc(T('hunt_empty_warn'))}</span></div>` : '';
        host.innerHTML = `${warn}<div class="banner info hf-prevnote">${esc(T('hunt_preview_note'))}</div><div class="hf-prevform"></div>`;
        state.form = create(host.querySelector('.hf-prevform'), { settings: prev, readOnly: true });
      }
      // wire template select / edit-here (they live in the mode bar, re-wire each render)
      const tplSel = container.querySelector('.hf-tpl'); if (tplSel) tplSel.onchange = () => { state.templateId = Number(tplSel.value); updateSaveBar(); updateGuard(); };
      const editMine = container.querySelector('.hf-editmine'); if (editMine) editMine.onclick = onEditMine;
    }
    function onMode(mode) {
      if (mode === state.mode) return;
      if (state.mode === 'custom' && ((state.form && state.form.isDirty()) || state.customDraft != null)) {
        App.dialog({
          title: T('hunt_switch_title'), className: 'narrow',
          body: `<p class="dialog-text">${esc(T('hunt_switch_msg'))}</p>`,
          actions: [
            { label: T('cancel'), kind: 'secondary' },
            { label: T('hunt_save_as_profile'), kind: 'secondary', onClick: async () => { const ok = await saveAsProfile(); if (!ok) return false; switchMode(mode); } },
            { label: T('hunt_switch_discard'), kind: 'danger', onClick: () => { switchMode(mode); } }
          ]
        });
        return;
      }
      switchMode(mode);
    }
    function switchMode(mode) {
      state.customDraft = null;
      state.mode = mode;
      if (mode === 'template' && state.templateId == null) { const tps = state.st.templates || []; const d = defaultTemplate() || tps[0]; state.templateId = d ? d.id : null; }
      render();
    }
    function onEditMine() {
      // copy the read-only preview values into an unsaved custom draft
      state.customDraft = clone(previewSettings());
      state.mode = 'custom';
      render();
    }
    function onRevert() {
      state.customDraft = null;
      state.mode = state.st.mode; state.templateId = state.st.template_id;
      render();
    }
    async function ensureFresh() {
      const r = await App.api('/api/slots/' + slotId + '/settings');
      if (r.status !== 200 || !r.data) { App.toast(App.errorText(r.data), 'error'); return false; }
      if (state.revision != null && r.data.revision != null && r.data.revision !== state.revision) {
        const reload = await App.confirm({ title: T('hunt_changed_title'), message: T('hunt_changed_msg'), confirm: T('hunt_reload'), cancel: T('keep_editing'), danger: true });
        if (reload) { adopt(r.data); render(); }
        return false;
      }
      return true;
    }
    function adopt(data) {
      state.st = data; state.mode = data.mode || 'custom'; state.templateId = data.template_id; state.revision = data.revision;
    }
    async function onSave() {
      if (state.saving) return;
      let body;
      if (state.mode === 'custom') {
        let settings; try { settings = state.form.collect(); } catch (err) { if (err instanceof ValidationError) { showErrors(err.errors); return; } throw err; }
        body = { mode: 'custom', settings };
      } else if (state.mode === 'template') {
        if (state.templateId == null) { App.toast(T('hunt_no_template'), 'error'); return; }
        body = { mode: 'template', template_id: Number(state.templateId) };
      } else body = { mode: 'default' };
      state.saving = true;
      const saveBtn = container.querySelector('.hf-save'); if (saveBtn) saveBtn.disabled = true;
      try {
        if (!(await ensureFresh())) return;
        const r = await App.api('/api/slots/' + slotId + '/settings', { method: 'PUT', body });
        if (r.status === 409) { await ensureFresh(); return; }
        if (!r.ok || !r.data || !r.data.ok) { App.toast(App.errorText(r.data), 'error'); return; }
        state.revision = r.data.revision;
        state.st.mode = r.data.mode; state.st.template_id = r.data.template_id;
        if (state.mode === 'custom') state.st.custom = body.settings;
        state.st.settings = state.mode === 'custom' ? body.settings : (state.mode === 'template' ? templateSettings(state.templateId) : (defaultTemplate() ? defaultTemplate().settings : {}));
        // refresh form baseline so dirty clears
        state.customDraft = null;
        if (state.mode === 'custom' && state.form) state.form.reset(body.settings);
        App.toast(r.data.agent_pushed ? T('hunt_pushed') : T('hunt_not_pushed'), r.data.agent_pushed ? 'ok' : 'info');
        updateSaveBar(); updateGuard();
        if (opts.onSaved) try { opts.onSaved(r.data); } catch (_) {}
      } finally { state.saving = false; if (saveBtn) saveBtn.disabled = false; }
    }
    async function saveAsProfile() {
      let settings; try { settings = state.form.collect(); } catch (err) { if (err instanceof ValidationError) { showErrors(err.errors); return false; } throw err; }
      const name = await promptName();
      if (!name) return false;
      const body = { name, settings }; if (App.viewAs) body.user_id = App.viewAs.id;
      const r = await App.api('/api/templates', { method: 'POST', body });
      if (!r.ok || !r.data) { App.toast(App.errorText(r.data), 'error'); return false; }
      App.toast(T('hunt_profile_created'));
      const tp = r.data.template || r.data;
      if (tp && tp.id) { state.st.templates = (state.st.templates || []).concat([tp]); state.templateId = tp.id; }
      return true;
    }
    function promptName() {
      return new Promise((resolve) => {
        let done = false;
        const input = document.createElement('input'); input.type = 'text'; input.className = 'hf-pname'; input.placeholder = T('hunt_profile_name');
        const wrap = document.createElement('div'); wrap.className = 'field'; wrap.appendChild(input);
        App.dialog({ title: T('hunt_save_as_profile'), body: wrap, className: 'narrow',
          actions: [ { label: T('cancel'), kind: 'secondary' }, { label: T('save'), kind: 'primary', onClick: () => { const v = input.value.trim(); if (!v) return false; done = true; resolve(v); } } ],
          onClose: () => { if (!done) resolve(null); } });
        setTimeout(() => input.focus(), 30);
      });
    }
    function showErrors(errors) {
      updateSaveBar(errors.length);
      const host = container.querySelector('.hf-formhost');
      const first = host.querySelector('.hf-field.invalid');
      if (first) { first.scrollIntoView({ behavior: 'smooth', block: 'center' }); const inp = first.querySelector('input,select,textarea'); if (inp) inp.focus(); }
    }
    function updateSaveBar(errCount) {
      const bar = container.querySelector('.hf-savebar'); if (!bar) return;
      const dirty = isDirty();
      bar.hidden = !dirty;
      const msg = bar.querySelector('.hf-savemsg');
      if (errCount) { msg.textContent = T('hunt_errors_n', errCount); msg.className = 'hf-savemsg error'; }
      else { const n = state.mode === 'custom' && state.form ? Math.max(state.form.dirtyCount(), state.customDraft != null ? 1 : 0) : (dirty ? 1 : 0); msg.textContent = dirty ? T('hunt_changes_n', n) : T('hunt_no_changes'); msg.className = 'hf-savemsg'; }
    }

    // boot
    (async function boot() {
      container.innerHTML = `<div class="hf-loading"><div class="skeleton" style="height:40px"></div><div class="skeleton" style="height:260px"></div></div>`;
      const r = await App.api('/api/slots/' + slotId + '/settings');
      if (state.destroyed) return;
      if (r.status !== 200 || !r.data) { container.innerHTML = `<div class="empty-state"><p>${esc(App.errorText(r.data))}</p></div>`; return; }
      adopt(r.data);
      render();
      // live positions over ws
      if (state.liveFn) { /* parent supplies live */ }
    })();

    return {
      isDirty,
      destroy() {
        state.destroyed = true;
        if (state.form) { try { state.form.destroy(); } catch (_) {} }
        if (unsubWs) unsubWs();
        App.setLeaveGuard(null);
        container.innerHTML = '';
      }
    };
  }

  // ────────────────────────────────────────────── page: #/slot-settings/<id> ──────────────────────────────────────────────
  App.page('slot-settings', {
    title: () => T('hunt_slot_page_title'),
    async render(root, params) {
      const slotId = params[0];
      root.innerHTML = `<div class="page"><div class="page-head"><div class="titles"><div class="skeleton" style="height:28px;width:220px"></div></div></div><div class="skeleton" style="height:320px"></div></div>`;
      // find slot / machine for the header + live character id
      const mr = await App.api('/api/machines');
      let machine = null, slot = null;
      if (mr.ok && mr.data && mr.data.machines) {
        for (const m of mr.data.machines) for (const s of (m.slots || [])) if (String(s.id) === String(slotId)) { machine = m; slot = s; }
      }
      const label = machine ? machine.label : '';
      const acc = slot ? slot.game_account : '';
      const nick = slot && slot.nickname ? slot.nickname : '';
      const srv = slot && slot.server_name ? slot.server_name : '';
      const sub = [acc, nick, srv].filter(Boolean).map(esc).join(' · ');
      root.innerHTML = `<div class="page">
        <div class="page-head">
          <div class="titles">
            <a class="linkish small" href="#/machines">${esc(T('hunt_back_machines'))}</a>
            <h1>${esc(label || T('hunt_slot_page_title'))}</h1>
            ${sub ? `<div class="desc">${sub}</div>` : ''}
          </div>
        </div>
        <div id="hf-slot-host"></div>
      </div>`;
      if (!slot && mr.ok) { root.querySelector('#hf-slot-host').innerHTML = `<div class="empty-state"><p>${esc(T('hunt_slot_not_found'))}</p><a class="btn secondary" href="#/machines">${esc(T('hunt_back_machines'))}</a></div>`; return; }
      // live position from the slot's character (if any) over ws
      let livePos = null;
      const charId = slot && slot.character && slot.character.id;
      const host = root.querySelector('#hf-slot-host');
      const editor = mountSlotEditor(host, { slotId, live: () => livePos, onSaved: null });
      let off = null;
      if (charId) {
        App.ws.subscribe([charId]);
        off = App.ws.on('states', (m) => {
          for (const s of (m.states || [])) if (String(s.character_id) === String(charId)) { if (s.map_id != null) livePos = { map_id: s.map_id, x: s.x, y: s.y }; }
        });
      }
      return () => { if (off) off(); editor.destroy(); };
    }
  });

  return { create, mountSlotEditor, ValidationError };
})();
